import { Fragment, useState, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- routine loaders use explicit tenant and modal lifecycle keys */
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';

interface Ejercicio {
  id: number;
  nombre: string;
  categoria: string;
  equipo: string;
  dificultad: string;
  descripcion: string;
  musculosPrincipales: string[];
  // Steps shown in mobile under "Como realizar el ejercicio".
  instrucciones?: string[];
  // Optional per-exercise tips; mobile can fall back to generic tips.
  consejos?: string[];
  tieneVideo: boolean;
  tieneAnimacion: boolean;
  tieneVideoSubido: boolean;
  urlVideo?: string;
  urlGif?: string;
  urlVideoSubido?: string;
  videoSubidoPath?: string;
  duracionVideoSegundos?: number | null;
  alcance?: 'general' | 'privado';
  createdByAdminId?: string | null;
}

interface Rutina {
  id: number;
  nombre: string;
  descripcion: string;
  nivel: string;
  duracion: number;
  fechaInicio?: string;
  calentamientoGeneral?: CalentamientoGeneral;
  ejercicios: number;
  usuariosAsignados: number;
  activa: boolean;
  es_plantilla?: boolean;
  plantilla_global?: boolean;
  creada_por_usuario?: boolean;
  es_copia_usuario?: boolean;
  created_by_admin_id?: string | null;
  rutina_origen_id?: string | null;
  sesiones?: Sesion[];
  progresionSemanal?: ProgresionSemanal;
  usuarios_asignados?: string[]; // IDs de usuarios asignados
}

interface EjercicioEnRutina {
  ejercicioId: number;
  nombre: string;
  categoria: string;
  series: number;
  repeticiones: number;
  descanso: number;
  peso?: number;
  rir?: number;
  tempo?: string;
}

interface Sesion {
  id: string;
  nombre: string;
  ejercicios: EjercicioEnRutina[];
}

interface SemanaProgresion { numero: number; sesiones: Sesion[] }
interface ProgresionSemanal {
  activa: boolean;
  semanas: SemanaProgresion[];
  origen?: 'acoma_renovacion' | string;
  ejercicios_con_evidencia?: number;
  criterio?: string;
}

interface CalentamientoGeneral {
  tipo: string;
  duracionMin: number;
  intensidad: string;
  notas: string;
}

// Add Usuario interface
interface Usuario {
  id: string;
  nombre: string;
  email: string;
  avatar: string;
  plan: string;
  estado: string;
  fechaAsignacion?: string;
  progreso?: number;
}

interface CoachAsignacion {
  id: string;
  nombre: string;
  email: string;
  tipo_staff: string;
}

interface EvaluacionInicialResumen {
  id: string;
  usuario_id: string;
  fecha_evaluacion: string;
  estado: 'completa' | 'requiere_revision';
  nivel_entrenamiento: string | null;
  experiencia_gimnasio: string | null;
  frecuencia_semanal: string | null;
  sentadilla_puntaje: string | null;
  bisagra_puntaje: string | null;
  bisagra_dolor: boolean;
  tobillo_derecho_cm: number | null;
  tobillo_derecho_nt: boolean;
  tobillo_izquierdo_cm: number | null;
  tobillo_izquierdo_nt: boolean;
  tobillo_diferencia_cm: number | null;
  hombro_derecho_puntaje: string | null;
  hombro_izquierdo_puntaje: string | null;
  hombro_dolor_derecho: boolean;
  hombro_dolor_izquierdo: boolean;
  dolor_sintoma: boolean;
  dolor_detalle: string | null;
  observaciones_generales: string | null;
}

interface GrupoMuscularCatalogo {
  id: number;
  nombre: string;
  activo: boolean;
  orden: number;
}

interface FamiliaRutina {
  id: string;
  nombre: string;
  descripcion: string | null;
  activa: boolean;
  avance_automatico: boolean;
  dias_aprobacion: number;
  rutina_familia_etapas: Array<{
    id: string;
    rutina_id: string;
    orden: number;
    duracion_dias: number;
    sesiones_minimas: number | null;
    rutina?: { id: string; nombre: string } | null;
  }>;
}
type NivelFamiliaDraft = { rutinaId: string; duracionDias: number; sesionesMinimas: string };
type FamiliaEditDraft = {
  nombre: string;
  descripcion: string;
  activa: boolean;
  avanceAutomatico: boolean;
  diasAprobacion: number;
};

const categorias = ['Pecho', 'Espalda', 'Piernas', 'Hombros', 'Brazos', 'Core', 'Cardio'];
const equipos = ['Barra', 'Mancuernas', 'Polea', 'Máquina', 'Barra Fija', 'Sin Equipo', 'Kettlebell', 'Banda Elástica'];
const dificultades = ['Principiante', 'Intermedio', 'Avanzado'];
const tiposCalentamiento = ['Cardiovascular', 'Movilidad', 'Mixto'];
const intensidadesCalentamiento = ['Suave', 'Moderada', 'Alta'];
const tempoOptions = ['3-2-1', '2-1-2', '4-1-2'];
const musculosDisponiblesDefault = [
  'Pectoral Mayor', 'Dorsal Ancho', 'Trapecio', 'Deltoides', 'Deltoides Anterior',
  'Bíceps', 'Bíceps Braquial', 'Braquial Anterior', 'Tríceps', 'Tríceps Braquial',
  'Cuádriceps', 'Isquiotibiales', 'Glúteos', 'Pantorrillas',
  'Abdominales', 'Oblicuos', 'Espalda Baja', 'Core'
];

const PESO_STEP_KG = 2.5;
const PESO_MAX_KG = 500;
const normalizarPesoKg = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const limited = Math.max(0, Math.min(PESO_MAX_KG, parsed));
  return Math.round(limited / PESO_STEP_KG) * PESO_STEP_KG;
};

const buildDefaultCalentamiento = (): CalentamientoGeneral => ({
  tipo: 'Cardiovascular',
  duracionMin: 10,
  intensidad: 'Moderada',
  notas: '',
});

const normalizeSesionesForPersist = (input: Sesion[]): Sesion[] =>
  (input || []).map((sesion) => ({
    ...sesion,
    ejercicios: (sesion.ejercicios || []).map((ej) => ({
      ...ej,
      peso: normalizarPesoKg(ej.peso),
      rir: Number.isFinite(Number(ej.rir)) ? Number(ej.rir) : 2,
      tempo: (typeof ej.tempo === 'string' && ej.tempo.trim().length > 0) ? ej.tempo.trim() : '3-2-1',
    })),
  }));

const normalizeProgresionForPersist = (input: ProgresionSemanal): ProgresionSemanal => ({
  ...input,
  semanas: (input.semanas || []).map((semana) => ({
    ...semana,
    sesiones: normalizeSesionesForPersist(semana.sesiones),
  })),
});

type VolumenMuscular = { nombre: string; series: number; ejercicios: number };

const calcularVolumenMuscular = (sesiones: Sesion[], catalogo: Ejercicio[]): VolumenMuscular[] => {
  const resumen = new Map<string, VolumenMuscular>();
  const catalogoPorId = new Map(catalogo.map((ejercicio) => [String(ejercicio.id), ejercicio]));

  sesiones.forEach((sesion) => {
    sesion.ejercicios.forEach((ejercicioRutina) => {
      const ejercicio = catalogoPorId.get(String(ejercicioRutina.ejercicioId));
      const grupos = ejercicio?.musculosPrincipales?.length
        ? ejercicio.musculosPrincipales
        : [ejercicio?.categoria || ejercicioRutina.categoria || 'Sin clasificar'];
      const gruposUnicos = [...new Set(grupos.map((grupo) => grupo.trim()).filter(Boolean))];

      gruposUnicos.forEach((grupo) => {
        const key = grupo.toLocaleLowerCase('es-CL');
        const actual = resumen.get(key) || { nombre: grupo, series: 0, ejercicios: 0 };
        actual.series += Math.max(0, Number(ejercicioRutina.series) || 0);
        actual.ejercicios += 1;
        resumen.set(key, actual);
      });
    });
  });

  return [...resumen.values()].sort((a, b) => b.series - a.series || a.nombre.localeCompare(b.nombre, 'es-CL'));
};

function VolumenMuscularResumen({ sesiones, catalogo }: { sesiones: Sesion[]; catalogo: Ejercicio[] }) {
  const volumen = calcularVolumenMuscular(sesiones, catalogo);
  const maximo = Math.max(1, ...volumen.map((item) => item.series));

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
      <div className="flex items-start gap-2 mb-3">
        <i className="ri-bar-chart-grouped-line text-violet-700 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-violet-950">Volumen por grupo muscular</p>
          <p className="text-xs text-violet-700">Se actualiza automáticamente según los ejercicios y series.</p>
        </div>
      </div>
      {volumen.length === 0 ? (
        <p className="text-xs text-violet-700">Agrega ejercicios para calcular el volumen programado.</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {volumen.map((item) => (
            <div key={item.nombre}>
              <div className="flex items-center justify-between gap-3 text-xs mb-1">
                <span className="font-medium text-gray-800 truncate">{item.nombre}</span>
                <span className="text-violet-800 whitespace-nowrap">{item.series} serie{item.series === 1 ? '' : 's'} · {item.ejercicios} ej.</span>
              </div>
              <div className="h-1.5 rounded-full bg-violet-100 overflow-hidden">
                <div className="h-full rounded-full bg-violet-600 transition-all" style={{ width: `${Math.max(5, (item.series / maximo) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const cloneSesiones = (value: Sesion[]): Sesion[] => JSON.parse(JSON.stringify(normalizeSesionesForPersist(value)));

function ProgresionSemanalEditor({baseSesiones,value,onChange}:{baseSesiones:Sesion[];value:ProgresionSemanal;onChange:(next:ProgresionSemanal)=>void}){
  const [active,setActive]=useState(1);
  const enable=(checked:boolean)=>onChange(checked?{activa:true,semanas:value.semanas.length?value.semanas:[{numero:1,sesiones:cloneSesiones(baseSesiones)}]}:{...value,activa:false});
  const current=value.semanas.find(w=>w.numero===active)||value.semanas[0];
  const update=(sessionId:string,exerciseId:number,field:keyof EjercicioEnRutina,next:number|string)=>onChange({...value,semanas:value.semanas.map(w=>w.numero!==current?.numero?w:{...w,sesiones:w.sesiones.map(s=>s.id!==sessionId?s:{...s,ejercicios:s.ejercicios.map(e=>e.ejercicioId===exerciseId?{...e,[field]:next}:e)})})});
  const add=()=>{const number=Math.max(0,...value.semanas.map(w=>w.numero))+1;const source=value.semanas[value.semanas.length-1]?.sesiones||baseSesiones;onChange({...value,semanas:[...value.semanas,{numero:number,sesiones:cloneSesiones(source)}]});setActive(number)};
  const remove=()=>{if(value.semanas.length<=1)return;const remaining=value.semanas.filter(w=>w.numero!==current.numero).map((w,i)=>({...w,numero:i+1}));onChange({...value,semanas:remaining});setActive(Math.min(active,remaining.length))};
  return <section className="mt-6 rounded-xl border border-violet-200 bg-violet-50/40 p-4 text-left"><div className="flex flex-wrap items-center justify-between gap-3"><div><h4 className="font-semibold text-gray-900"><i className="ri-line-chart-line text-violet-600 mr-2"/>Progresión semanal</h4><p className="text-xs text-gray-500 mt-1">Configura todos los ejercicios de una semana sin desplegar tablas por ejercicio.</p></div><label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={value.activa} onChange={e=>enable(e.target.checked)} className="h-4 w-4"/>Activar progresión</label></div>{value.origen==='acoma_renovacion'&&<div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"><p className="font-semibold"><i className="ri-sparkling-2-line mr-1"/>Borrador calculado desde el historial del socio</p><p className="mt-1">{value.ejercicios_con_evidencia||0} ejercicio{value.ejercicios_con_evidencia===1?'':'s'} con evidencia suficiente. {value.criterio||''} Revisa cada semana antes de aprobar.</p></div>}{value.activa&&current&&<><div className="flex items-center gap-2 overflow-x-auto mt-4 pb-2">{value.semanas.map(w=><button type="button" key={w.numero} onClick={()=>setActive(w.numero)} className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap ${current.numero===w.numero?'bg-violet-600 text-white':'bg-white border text-gray-700'}`}>Semana {w.numero}</button>)}<button type="button" onClick={add} className="px-3 py-2 rounded-lg border border-violet-300 bg-white text-violet-700 text-sm whitespace-nowrap"><i className="ri-add-line mr-1"/>Agregar semana</button><button type="button" disabled={value.semanas.length<=1} onClick={remove} className="px-3 py-2 rounded-lg border border-red-200 bg-white text-red-600 text-sm disabled:opacity-40">Eliminar</button></div><div className="space-y-4 mt-3 max-h-[440px] overflow-auto">{current.sesiones.map(s=><div key={s.id} className="bg-white border rounded-xl overflow-hidden"><div className="px-4 py-3 bg-gray-50 font-semibold text-sm">{s.nombre} <span className="text-gray-400 font-normal">({s.ejercicios.length} ejercicios)</span></div><div className="overflow-x-auto"><table className="w-full text-xs min-w-[850px]"><thead className="text-gray-500"><tr><th className="p-2 text-left">Ejercicio</th><th>Series</th><th>Reps</th><th>Peso</th><th>RIR</th><th>Tempo</th><th>Descanso</th></tr></thead><tbody className="divide-y">{s.ejercicios.map(e=><tr key={e.ejercicioId}><td className="p-2 font-medium">{e.nombre}</td>{([['series','number'],['repeticiones','number'],['peso','number'],['rir','number'],['tempo','text'],['descanso','number']] as Array<[keyof EjercicioEnRutina,string]>).map(([field,type])=><td key={field} className="p-1"><input type={type} min={type==='number'?0:undefined} step={field==='peso'?PESO_STEP_KG:undefined} value={String(e[field]??'')} onChange={event=>update(s.id,e.ejercicioId,field,type==='number'?Number(event.target.value):event.target.value)} onBlur={field==='peso'?event=>update(s.id,e.ejercicioId,field,normalizarPesoKg(event.target.value)):undefined} className="w-20 px-2 py-1.5 border rounded"/></td>)}</tr>)}</tbody></table></div></div>)}</div><p className="text-xs text-violet-700 mt-3"><i className="ri-file-copy-line mr-1"/>Cada semana nueva copia la anterior para que solo ajustes los cambios.</p></>}</section>
}

// Sesiones de ejemplo para rutinas existentes
const sesionesEjemplo: Record<number, Sesion[]> = {
  1: [
    {
      id: 'A',
      nombre: 'Sesión A - Tren Superior',
      ejercicios: [
        { ejercicioId: 1, nombre: 'Press de Banca', categoria: 'Pecho', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
        { ejercicioId: 4, nombre: 'Dominadas', categoria: 'Espalda', series: 3, repeticiones: 10, descanso: 60, peso: 0 },
        { ejercicioId: 5, nombre: 'Press Militar', categoria: 'Hombros', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
        { ejercicioId: 6, nombre: 'Curl de Bíceps', categoria: 'Brazos', series: 3, repeticiones: 15, descanso: 45, peso: 0 },
      ]
    },
    {
      id: 'B',
      nombre: 'Sesión B - Tren Inferior',
      ejercicios: [
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 4, repeticiones: 10, descanso: 90, peso: 0 },
        { ejercicioId: 3, nombre: 'Peso Muerto', categoria: 'Espalda', series: 3, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 3, repeticiones: 30, descanso: 45, peso: 0 },
      ]
    }
  ],
  2: [
    {
      id: 'A',
      nombre: 'Sesión A - Push',
      ejercicios: [
        { ejercicioId: 1, nombre: 'Press de Banca', categoria: 'Pecho', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 5, nombre: 'Press Militar', categoria: 'Hombros', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 7, nombre: 'Extensión de Tríceps', categoria: 'Brazos', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'B',
      nombre: 'Sesión B - Pull',
      ejercicios: [
        { ejercicioId: 4, nombre: 'Dominadas', categoria: 'Espalda', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 3, nombre: 'Peso Muerto', categoria: 'Espalda', series: 4, repeticiones: 6, descanso: 120, peso: 0 },
        { ejercicioId: 6, nombre: 'Curl de Bíceps', categoria: 'Brazos', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'C',
      nombre: 'Sesión C - Piernas',
      ejercicios: [
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 5, repeticiones: 5, descanso: 120, peso: 0 },
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 4, repeticiones: 45, descanso: 60, peso: 0 },
      ]
    }
  ],
  3: [
    {
      id: 'A',
      nombre: 'Sesión A - Pecho y Tríceps',
      ejercicios: [
        { ejercicioId: 1, nombre: 'Press de Banca', categoria: 'Pecho', series: 5, repeticiones: 5, descanso: 120, peso: 0 },
        { ejercicioId: 7, nombre: 'Extensión de Tríceps', categoria: 'Brazos', series: 4, repeticiones: 10, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'B',
      nombre: 'Sesión B - Espalda y Bíceps',
      ejercicios: [
        { ejercicioId: 3, nombre: 'Peso Muerto', categoria: 'Espalda', series: 5, repeticiones: 5, descanso: 180, peso: 0 },
        { ejercicioId: 4, nombre: 'Dominadas', categoria: 'Espalda', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 6, nombre: 'Curl de Bíceps', categoria: 'Brazos', series: 4, repeticiones: 10, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'C',
      nombre: 'Sesión C - Piernas',
      ejercicios: [
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 5, repeticiones: 5, descanso: 180, peso: 0 },
      ]
    },
    {
      id: 'D',
      nombre: 'Sesión D - Hombros y Core',
      ejercicios: [
        { ejercicioId: 5, nombre: 'Press Militar', categoria: 'Hombros', series: 5, repeticiones: 5, descanso: 120, peso: 0 },
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 4, repeticiones: 60, descanso: 60, peso: 0 },
      ]
    }
  ],
  4: [
    {
      id: 'A',
      nombre: 'Sesión Cardio HIIT',
      ejercicios: [
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 4, repeticiones: 30, descanso: 30, peso: 0 },
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 4, repeticiones: 20, descanso: 30, peso: 0 },
      ]
    }
  ]
};

type RutinasEmbedRequest = {
  rutinaId: string | number;
  action: 'detail' | 'edit';
  usuarioId?: string;
  recommendationId?: string;
};

type RutinasProps = {
  embeddedRequest?: RutinasEmbedRequest | null;
  onEmbeddedClose?: (saved?: boolean) => void;
};

export default function Rutinas({ embeddedRequest, onEmbeddedClose }: RutinasProps = {}) {
  const legacyEditModalEnabled = false;
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const isDirectEmbed = Boolean(embeddedRequest);
  const isEmbedView = isDirectEmbed || searchParams.get('embed') === '1';
  const embedUsuarioId = (embeddedRequest?.usuarioId || searchParams.get('usuarioId') || '').trim();
  const notifyEmbedClose = (saved = false) => {
    if (!isEmbedView) return;
    if (onEmbeddedClose) {
      onEmbeddedClose(saved);
      return;
    }
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'rutina-embed-close', saved }, '*');
      }
    } catch (err) {
      console.warn('No se pudo notificar cierre embed:', err);
    }
  };
  const MAX_EXERCISE_VIDEO_SIZE_MB = 10;
  const MAX_EXERCISE_VIDEO_SIZE_BYTES = MAX_EXERCISE_VIDEO_SIZE_MB * 1024 * 1024;
  const ALLOWED_EXERCISE_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
  const ALLOWED_EXERCISE_VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov'];
  const { sucursalActiva } = useSucursal();
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'rutinas' | 'ejercicios' | 'plantillas' | 'personalizadas' | 'familias'>('rutinas');
  const [showOnlyMyRutinas, setShowOnlyMyRutinas] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rutinas, setRutinas] = useState<Rutina[]>([]);
  const [plantillas, setPlantillas] = useState<Rutina[]>([]);
  const [familias, setFamilias] = useState<FamiliaRutina[]>([]);
  const [familiasLoading, setFamiliasLoading] = useState(false);
  const [showFamiliaForm, setShowFamiliaForm] = useState(false);
  const [familiaNombre, setFamiliaNombre] = useState('');
  const [familiaDescripcion, setFamiliaDescripcion] = useState('');
  const [familiaDiasAprobacion, setFamiliaDiasAprobacion] = useState(5);
  const [familiaAvanceAutomatico, setFamiliaAvanceAutomatico] = useState(true);
  const [familiaSaving, setFamiliaSaving] = useState(false);
  const [familiaNiveles, setFamiliaNiveles] = useState<NivelFamiliaDraft[]>([{ rutinaId: '', duracionDias: 30, sesionesMinimas: '' }]);
  const [familiaEditandoId, setFamiliaEditandoId] = useState<string | null>(null);
  const [familiaEditDraft, setFamiliaEditDraft] = useState<FamiliaEditDraft | null>(null);
  const [familiaEditandoEtapaId, setFamiliaEditandoEtapaId] = useState<string | null>(null);
  const [familiaEtapaDraft, setFamiliaEtapaDraft] = useState<NivelFamiliaDraft>({ rutinaId: '', duracionDias: 30, sesionesMinimas: '' });
  const [familiaEditandoNivelesId, setFamiliaEditandoNivelesId] = useState<string | null>(null);
  const [nuevoNivelDraft, setNuevoNivelDraft] = useState<NivelFamiliaDraft>({ rutinaId: '', duracionDias: 30, sesionesMinimas: '' });
  const [ejercicios, setEjercicios] = useState<Ejercicio[]>([]);
  const [gruposMusculares, setGruposMusculares] = useState<GrupoMuscularCatalogo[]>([]);
  const [musculosDisponibles, setMusculosDisponibles] = useState<string[]>(musculosDisponiblesDefault);
  const [showNuevoMusculoInput, setShowNuevoMusculoInput] = useState(false);
  const [nuevoMusculoNombre, setNuevoMusculoNombre] = useState('');
  const [guardandoNuevoMusculo, setGuardandoNuevoMusculo] = useState(false);
  const [showGestionMusculos, setShowGestionMusculos] = useState(false);
  const [editingMusculoId, setEditingMusculoId] = useState<number | null>(null);
  const [editingMusculoNombre, setEditingMusculoNombre] = useState('');
  const [procesandoMusculoId, setProcesandoMusculoId] = useState<number | null>(null);
  const [loadingEjercicios, setLoadingEjercicios] = useState(false);
  const [errorEjercicios, setErrorEjercicios] = useState<string | null>(null);
  
  // Add missing loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingRutinaId, setDeletingRutinaId] = useState<number | null>(null);
  const [deletingExerciseId, setDeletingExerciseId] = useState<number | null>(null);
  
  const [isNewExerciseModalOpen, setIsNewExerciseModalOpen] = useState(false);
  const [isNewRutinaModalOpen, setIsNewRutinaModalOpen] = useState(false);
  const [isEditRutinaModalOpen, setIsEditRutinaModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRutinaForAssign, setSelectedRutinaForAssign] = useState<Rutina | null>(null);
  const [selectedFamiliaForAssign, setSelectedFamiliaForAssign] = useState<FamiliaRutina | null>(null);
  const [familiaAlreadyAssignedIds, setFamiliaAlreadyAssignedIds] = useState<string[]>([]);
  const [selectedRutinaForDetail, setSelectedRutinaForDetail] = useState<Rutina | null>(null);
  const [selectedRutinaForEdit, setSelectedRutinaForEdit] = useState<Rutina | null>(null);
  const [detailActiveTab, setDetailActiveTab] = useState<'info' | 'sesiones' | 'usuarios'>('info');
  const [detailActiveSesion, setDetailActiveSesion] = useState<string>('A');

  // Estados para asignación de rutinas
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [coachesAsignacion, setCoachesAsignacion] = useState<CoachAsignacion[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [loadingCoachesAsignacion, setLoadingCoachesAsignacion] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [initialEvaluationsByUser, setInitialEvaluationsByUser] = useState<Record<string, EvaluacionInicialResumen>>({});
  const [expandedEvaluationUserId, setExpandedEvaluationUserId] = useState<string | null>(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isExportingTemplate, setIsExportingTemplate] = useState(false);
  const [exportTemplateSourceId, setExportTemplateSourceId] = useState<string | null>(null);
  const [exportSuccessName, setExportSuccessName] = useState<string | null>(null);
  const [isTemplateMode, setIsTemplateMode] = useState(false);
  const [templateGlobalScope, setTemplateGlobalScope] = useState(false);
  const [editTemplateGlobalScope, setEditTemplateGlobalScope] = useState(false);
  
  // Add usuarios state to fetch from Supabase
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  
  // Estados para editar ejercicio
  const [isEditExerciseModalOpen, setIsEditExerciseModalOpen] = useState(false);
  const [selectedExerciseForEdit, setSelectedExerciseForEdit] = useState<Ejercicio | null>(null);
  const [originalExerciseForEdit, setOriginalExerciseForEdit] = useState<Ejercicio | null>(null);
  const [newExerciseVideoFile, setNewExerciseVideoFile] = useState<File | null>(null);
  const [editExerciseVideoFile, setEditExerciseVideoFile] = useState<File | null>(null);
  
  // Estado para detalle de ejercicio
  const [isExerciseDetailModalOpen, setIsExerciseDetailModalOpen] = useState(false);
  const [selectedExerciseForDetail, setSelectedExerciseForDetail] = useState<Ejercicio | null>(null);

  // Add state for usuarios asignados in detail modal
  const [usuariosAsignadosDetail, setUsuariosAsignadosDetail] = useState<Usuario[]>([]);
  const [loadingUsuariosDetail, setLoadingUsuariosDetail] = useState(false);
  const [activeUserMenu, setActiveUserMenu] = useState<string | null>(null);
  const [usuariosAsignadosSearchTerm, setUsuariosAsignadosSearchTerm] = useState('');
  const [usuariosAsignadosPage, setUsuariosAsignadosPage] = useState(1);
  const [usuariosAsignadosEstadoFilter, setUsuariosAsignadosEstadoFilter] = useState('');
  const [usuariosAsignadosPlanFilter, setUsuariosAsignadosPlanFilter] = useState('');
  const USUARIOS_PER_PAGE = 5;
  
  const [newExercise, setNewExercise] = useState<Omit<Ejercicio, 'id'>>({
    nombre: '',
    categoria: '',
    equipo: '',
    dificultad: '',
    descripcion: '',
    musculosPrincipales: [],
    instrucciones: [],
    consejos: [],
    tieneVideo: false,
    tieneAnimacion: false,
    tieneVideoSubido: false,
    urlVideo: '',
    urlGif: '',
    urlVideoSubido: '',
    videoSubidoPath: '',
    duracionVideoSegundos: null
  });

  const normalizeMusculoNombre = (nombre: string) => nombre.trim().replace(/\s+/g, ' ');
  const musculoKey = (nombre: string) => normalizeMusculoNombre(nombre).toLocaleLowerCase('es-CL');

  const mergeMusculosUnicos = (nombres: string[]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of nombres) {
      const value = normalizeMusculoNombre(raw);
      if (!value) continue;
      const key = musculoKey(value);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(value);
    }
    return out;
  };

  const sanitizeFileName = (name: string) =>
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 120);

  const isHttpUrl = (value?: string | null) => /^https?:\/\//i.test((value || '').trim());

  const validateExerciseVideoFile = (file: File): void => {
    if (file.size > MAX_EXERCISE_VIDEO_SIZE_BYTES) {
      throw new Error(`El video debe pesar máximo ${MAX_EXERCISE_VIDEO_SIZE_MB} MB.`);
    }

    const fileName = (file.name || '').toLowerCase();
    const hasAllowedMime = ALLOWED_EXERCISE_VIDEO_MIME_TYPES.includes((file.type || '').toLowerCase());
    const hasAllowedExtension = ALLOWED_EXERCISE_VIDEO_EXTENSIONS.some((ext) => fileName.endsWith(ext));
    if (!hasAllowedMime && !hasAllowedExtension) {
      throw new Error('Formato no soportado. Usa MP4, WEBM, OGG o MOV (QuickTime).');
    }
  };

  const uploadExerciseVideo = async (file: File) => {
    if (!sucursalActiva?.gimnasio_id) {
      throw new Error('No hay gimnasio activo para subir el video.');
    }

    validateExerciseVideoFile(file);
    const safeName = sanitizeFileName(file.name || 'ejercicio.mp4');
    const path = `${sucursalActiva.gimnasio_id}/${Date.now()}_${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from('exercise-videos')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'video/mp4',
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage.from('exercise-videos').getPublicUrl(path);
    if (!data?.publicUrl) {
      throw new Error('No se pudo obtener la URL del video subido.');
    }

    return { publicUrl: data.publicUrl, path, duration: null as number | null };
  };

  const removeExerciseVideo = async (path?: string | null) => {
    if (!path) return;
    await supabase.storage.from('exercise-videos').remove([path]);
  };

  const applyExclusiveMediaSelection = <T extends Omit<Ejercicio, 'id'> | Ejercicio>(
    current: T,
    mode: 'video' | 'gif' | 'upload',
    enabled: boolean
  ): T => {
    const base = { ...current } as T;

    if (!enabled) {
      if (mode === 'video') {
        return { ...base, tieneVideo: false, urlVideo: '' } as T;
      }
      if (mode === 'gif') {
        return { ...base, tieneAnimacion: false, urlGif: '' } as T;
      }
      return {
        ...base,
        tieneVideoSubido: false,
        urlVideoSubido: '',
        videoSubidoPath: '',
        duracionVideoSegundos: null,
      } as T;
    }

    if (mode === 'video') {
      return {
        ...base,
        tieneVideo: true,
        tieneAnimacion: false,
        tieneVideoSubido: false,
        urlGif: '',
        urlVideoSubido: '',
        videoSubidoPath: '',
        duracionVideoSegundos: null,
      } as T;
    }

    if (mode === 'gif') {
      return {
        ...base,
        tieneAnimacion: true,
        tieneVideo: false,
        tieneVideoSubido: false,
        urlVideo: '',
        urlVideoSubido: '',
        videoSubidoPath: '',
        duracionVideoSegundos: null,
      } as T;
    }

    return {
      ...base,
      tieneVideoSubido: true,
      tieneVideo: false,
      tieneAnimacion: false,
      urlVideo: '',
      urlGif: '',
    } as T;
  };

  // Estado para nueva rutina con sesiones
  const [newRutina, setNewRutina] = useState({
    nombre: '',
    descripcion: '',
    nivel: '',
    duracion: 45,
    fechaInicio: new Date().toISOString().split('T')[0],
    calentamientoGeneral: buildDefaultCalentamiento(),
    activa: true
  });
  const [sesiones, setSesiones] = useState<Sesion[]>([
    { id: 'A', nombre: 'Sesión A', ejercicios: [] }
  ]);
  const [sesionActiva, setSesionActiva] = useState('A');
  const [progresionSemanal,setProgresionSemanal]=useState<ProgresionSemanal>({activa:false,semanas:[]});
  const [ejercicioSearchTerm, setEjercicioSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');

  // Estado para editar rutina
  const [editRutina, setEditRutina] = useState({
    id: 0,
    nombre: '',
    descripcion: '',
    nivel: '',
    duracion: 45,
    fechaInicio: new Date().toISOString().split('T')[0],
    calentamientoGeneral: buildDefaultCalentamiento(),
    activa: true
  });
  const [editSesiones, setEditSesiones] = useState<Sesion[]>([]);
  const [editSesionActiva, setEditSesionActiva] = useState('A');
  const [editProgresionSemanal,setEditProgresionSemanal]=useState<ProgresionSemanal>({activa:false,semanas:[]});
  const [editEjercicioSearchTerm, setEditEjercicioSearchTerm] = useState('');
  const [editCategoriaFiltro, setEditCategoriaFiltro] = useState('');
  const [pendingDeepLink, setPendingDeepLink] = useState<{ rutinaId: string; action: 'detail' | 'edit' } | null>(null);

  const rutinasOficiales = rutinas.filter((rutina) => !rutina.creada_por_usuario);
  const rutinasPersonalizadas = rutinas.filter((rutina) => rutina.creada_por_usuario);

  const filteredRutinas = rutinasOficiales.filter(rutina =>
    !rutina.es_copia_usuario &&
    (!showOnlyMyRutinas || rutina.created_by_admin_id === profile?.adminId) &&
    (
      rutina.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rutina.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const filteredRutinasPersonalizadas = rutinasPersonalizadas.filter(rutina =>
    !rutina.es_copia_usuario &&
    (
      rutina.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rutina.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const filteredPlantillas = plantillas.filter(rutina =>
    rutina.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rutina.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFamilias = familias.filter((familia) =>
    familia.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (familia.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEjercicios = ejercicios.filter(ejercicio =>
    ejercicio.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ejercicio.categoria.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (embeddedRequest) {
      setPendingDeepLink({
        rutinaId: String(embeddedRequest.rutinaId),
        action: embeddedRequest.action,
      });
      return;
    }
    const params = new URLSearchParams(location.search);
    const rutinaIdRaw = params.get('rutinaId');
    const actionRaw = params.get('action');
        const action = actionRaw === 'edit' ? 'edit' : actionRaw === 'detail' ? 'detail' : null;

    if (!rutinaIdRaw || !action) {
      setPendingDeepLink(null);
      return;
    }

    setPendingDeepLink({ rutinaId: rutinaIdRaw, action });
  }, [embeddedRequest?.rutinaId, embeddedRequest?.action, location.search]);

  useEffect(() => {
    if (!pendingDeepLink || loading) return;
    const target = rutinas.find((r) => String(r.id) === pendingDeepLink.rutinaId);
    if (!target) return;

    setActiveTab(target.creada_por_usuario ? 'personalizadas' : 'rutinas');
    if (pendingDeepLink.action === 'edit') {
      handleOpenEditRutinaModal(target);
    } else {
      handleOpenDetailModal(target);
    }

    setPendingDeepLink(null);
    if (!isDirectEmbed) {
      navigate(isEmbedView ? '/rutinas?embed=1' : '/rutinas', { replace: true });
    }
  }, [pendingDeepLink, loading, rutinas, navigate, isEmbedView, isDirectEmbed]);
  // Ejercicios filtrados para el modal de nueva rutina
  const ejerciciosDisponibles = ejercicios.filter(ejercicio => {
    const matchSearch = ejercicio.nombre.toLowerCase().includes(ejercicioSearchTerm.toLowerCase());
    const matchCategoria = !categoriaFiltro || ejercicio.categoria === categoriaFiltro;
    const sesionActual = sesiones.find(s => s.id === sesionActiva);
    const notAdded = !sesionActual?.ejercicios.some(e => e.ejercicioId === ejercicio.id);
    return matchSearch && matchCategoria && notAdded;
  });

  const handleOpenNewExerciseModal = () => {
    setNewExercise({
      nombre: '',
      categoria: '',
      equipo: '',
      dificultad: '',
      descripcion: '',
      musculosPrincipales: [],
      instrucciones: [],
      consejos: [],
      tieneVideo: false,
      tieneAnimacion: false,
      tieneVideoSubido: false,
      urlVideo: '',
      urlGif: '',
      urlVideoSubido: '',
      videoSubidoPath: '',
      duracionVideoSegundos: null
    });
    setNewExerciseVideoFile(null);
    setShowNuevoMusculoInput(false);
    setNuevoMusculoNombre('');
    setShowGestionMusculos(false);
    setEditingMusculoId(null);
    setEditingMusculoNombre('');
    setIsNewExerciseModalOpen(true);
  };

  const handleCloseNewExerciseModal = () => {
    setIsNewExerciseModalOpen(false);
    setNewExerciseVideoFile(null);
    setShowNuevoMusculoInput(false);
    setNuevoMusculoNombre('');
    setShowGestionMusculos(false);
    setEditingMusculoId(null);
    setEditingMusculoNombre('');
  };

  const handleOpenNewRutinaModal = (templateMode = false) => {
    setIsExportingTemplate(false);
    setExportTemplateSourceId(null);
    setIsTemplateMode(templateMode);
    setTemplateGlobalScope(false);
    setNewRutina({
      nombre: '',
      descripcion: '',
      nivel: '',
      duracion: 45,
      fechaInicio: new Date().toISOString().split('T')[0],
      calentamientoGeneral: buildDefaultCalentamiento(),
      activa: true
    });
    setSesiones([{ id: 'A', nombre: 'Sesión A', ejercicios: [] }]);
    setProgresionSemanal({activa:false,semanas:[]});
    setSesionActiva('A');
    setEjercicioSearchTerm('');
    setCategoriaFiltro('');
    setIsNewRutinaModalOpen(true);
  };

  const handleCloseNewRutinaModal = () => {
    setIsNewRutinaModalOpen(false);
    setIsExportingTemplate(false);
    setExportTemplateSourceId(null);
    setIsTemplateMode(false);
    setTemplateGlobalScope(false);
  };

  const handleExportPlantillaToRutina = (plantilla: Rutina) => {
    const sesionesPlantilla = JSON.parse(
      JSON.stringify(plantilla.sesiones || sesionesEjemplo[plantilla.id] || [])
    ) as Sesion[];

    if (!sesionesPlantilla.length) {
      alert('La plantilla no tiene sesiones para exportar.');
      return;
    }

    setIsExportingTemplate(true);
    setExportTemplateSourceId(String(plantilla.id));
    setIsTemplateMode(false);
    setTemplateGlobalScope(false);
    setNewRutina({
      nombre: `${plantilla.nombre} (copia)`,
      descripcion: plantilla.descripcion || '',
      nivel: plantilla.nivel || 'Intermedio',
      duracion: plantilla.duracion || 45,
      fechaInicio: plantilla.fechaInicio || new Date().toISOString().split('T')[0],
      calentamientoGeneral: plantilla.calentamientoGeneral || buildDefaultCalentamiento(),
      activa: plantilla.activa ?? true
    });
    setSesiones(sesionesPlantilla);
    setProgresionSemanal(plantilla.progresionSemanal?JSON.parse(JSON.stringify(plantilla.progresionSemanal)):{activa:false,semanas:[]});
    setSesionActiva(sesionesPlantilla[0]?.id || 'A');
    setEjercicioSearchTerm('');
    setCategoriaFiltro('');
    setIsNewRutinaModalOpen(true);
  };

  const aplicarEstadoUsuarioRutina = async (rutina: Rutina): Promise<Rutina> => {
    if (!embedUsuarioId) return rutina;
    if (!rutina?.id) return rutina;

    try {
      let rows: any[] = [];
      const { data: dataNueva, error: errorNueva } = await supabase
        .from('rutina_ejercicio_estado_usuario')
        .select('sesion_id,ejercicio_id,series_usadas,repeticiones_usadas,descanso_usado,rir_usado,tempo_usado,peso_usado')
        .eq('usuario_id', embedUsuarioId)
        .eq('rutina_id', String(rutina.id));

      if (!errorNueva && Array.isArray(dataNueva)) {
        rows = dataNueva as any[];
      } else {
        const { data: dataLegacy, error: errorLegacy } = await supabase
          .from('rutina_ejercicio_estado_usuario')
          .select('sesion_id,ejercicio_id,series,repeticiones,descanso,rir,tempo,peso_usado')
          .eq('usuario_id', embedUsuarioId)
          .eq('rutina_id', String(rutina.id));
        if (errorLegacy || !Array.isArray(dataLegacy)) return rutina;
        rows = (dataLegacy as any[]).map((r) => ({
          ...r,
          series_usadas: r.series,
          repeticiones_usadas: r.repeticiones,
          descanso_usado: r.descanso,
          rir_usado: r.rir,
          tempo_usado: r.tempo,
        }));
      }

      if (rows.length === 0) return rutina;
      const buildSesionAliases = (sesion: Sesion): string[] => {
        const rawId = String((sesion as any)?.id ?? '').trim();
        const rawNombre = String((sesion as any)?.nombre ?? '').trim();
        const aliases = new Set<string>();
        if (rawId) aliases.add(rawId);
        if (rawNombre) aliases.add(rawNombre);
        const matchFromId = rawId.match(/[A-Za-z]$/);
        const matchFromNombre = rawNombre.match(/[A-Za-z]$/);
        if (matchFromId?.[0]) aliases.add(matchFromId[0]);
        if (matchFromNombre?.[0]) aliases.add(matchFromNombre[0]);
        return Array.from(aliases);
      };

      const sesionesBase = (rutina.sesiones || sesionesEjemplo[rutina.id] || []) as Sesion[];
      const sesionesMerged = sesionesBase.map((sesion) => ({
        ...sesion,
        ejercicios: (sesion.ejercicios || []).map((ej) => {
          const ejercicioId = Number((ej as any).ejercicioId ?? (ej as any).id ?? 0);
          const sesionAliases = buildSesionAliases(sesion);
          const state = rows.find((row) => {
            const rowEjercicioId = Number((row as any).ejercicio_id || 0);
            if (!ejercicioId || rowEjercicioId !== ejercicioId) return false;
            const rowSesion = String((row as any).sesion_id || '').trim();
            return sesionAliases.includes(rowSesion);
          });
          if (!state) return ej;
          return {
            ...ej,
            series: state.series_usadas ?? ej.series,
            repeticiones: state.repeticiones_usadas ?? ej.repeticiones,
            descanso: state.descanso_usado ?? ej.descanso,
            rir: state.rir_usado ?? ej.rir,
            tempo: state.tempo_usado ?? ej.tempo,
            peso: state.peso_usado ?? ej.peso,
          };
        }),
      }));

      return { ...rutina, sesiones: sesionesMerged };
    } catch {
      return rutina;
    }
  };

  const handleOpenDetailModal = async (rutina: Rutina) => {
    const rutinaConEstado = await aplicarEstadoUsuarioRutina(rutina);
    const sesionesDetalle = rutinaConEstado.sesiones || sesionesEjemplo[rutinaConEstado.id] || [];
    setSelectedRutinaForDetail(rutinaConEstado);
    setDetailActiveTab('info');
    setDetailActiveSesion(sesionesDetalle[0]?.id || 'A');
    setIsDetailModalOpen(true);
    setUsuariosAsignadosSearchTerm('');
    setUsuariosAsignadosPage(1);
    setUsuariosAsignadosEstadoFilter('');
    setUsuariosAsignadosPlanFilter('');
    
    // Fetch usuarios asignados cuando se abre el modal de detalle
    if (rutinaConEstado.usuarios_asignados && rutinaConEstado.usuarios_asignados.length > 0) {
      fetchUsuariosAsignados(rutinaConEstado.usuarios_asignados);
    } else {
      setUsuariosAsignadosDetail([]);
    }
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedRutinaForDetail(null);
    setUsuariosAsignadosDetail([]);
    setUsuariosAsignadosSearchTerm('');
    setUsuariosAsignadosPage(1);
    setUsuariosAsignadosEstadoFilter('');
    setUsuariosAsignadosPlanFilter('');
    notifyEmbedClose();
  };

  // Función para manejar el clic del botón principal
  const handleButtonClick = () => {
    if (activeTab === 'rutinas' || activeTab === 'personalizadas') {
      handleOpenNewRutinaModal(false);
    } else if (activeTab === 'plantillas') {
      handleOpenNewRutinaModal(true);
    } else if (activeTab === 'familias') {
      setShowFamiliaForm(true);
    } else {
      handleOpenNewExerciseModal();
    }
  };

  // Funciones para detalle de ejercicio
  const handleOpenExerciseDetailModal = (ejercicio: Ejercicio) => {
    setSelectedExerciseForDetail(ejercicio);
    setIsExerciseDetailModalOpen(true);
  };

  const handleCloseExerciseDetailModal = () => {
    setIsExerciseDetailModalOpen(false);
    setSelectedExerciseForDetail(null);
  };

  // Funciones para editar ejercicio
  const handleOpenEditExerciseModal = (ejercicio: Ejercicio) => {
    setSelectedExerciseForEdit(ejercicio);
    setOriginalExerciseForEdit(ejercicio);
    setEditExerciseVideoFile(null);
    setShowNuevoMusculoInput(false);
    setNuevoMusculoNombre('');
    setShowGestionMusculos(false);
    setEditingMusculoId(null);
    setEditingMusculoNombre('');
    setIsEditExerciseModalOpen(true);
  };

  const handleCloseEditExerciseModal = () => {
    setIsEditExerciseModalOpen(false);
    setSelectedExerciseForEdit(null);
    setOriginalExerciseForEdit(null);
    setEditExerciseVideoFile(null);
    setShowNuevoMusculoInput(false);
    setNuevoMusculoNombre('');
    setShowGestionMusculos(false);
    setEditingMusculoId(null);
    setEditingMusculoNombre('');
  };

  const fetchMusculosCatalogo = async () => {
    if (!sucursalActiva?.gimnasio_id) {
      setMusculosDisponibles(musculosDisponiblesDefault);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('grupos_musculares')
        .select('id,nombre,activo,orden')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .eq('activo', true)
        .order('orden', { ascending: true })
        .order('nombre', { ascending: true });

      if (fetchError) throw fetchError;

      const catalogRows = (data || []) as GrupoMuscularCatalogo[];
      setGruposMusculares(catalogRows);

      const fromCatalog = catalogRows
        .map((row: any) => String(row?.nombre || ''))
        .filter(Boolean);

      const fromEjercicios = ejercicios.flatMap((e) =>
        Array.isArray(e.musculosPrincipales) ? e.musculosPrincipales : []
      );

      const merged = mergeMusculosUnicos([
        ...(fromCatalog.length ? fromCatalog : musculosDisponiblesDefault),
        ...fromEjercicios,
      ]);

      setMusculosDisponibles(merged.length ? merged : musculosDisponiblesDefault);
    } catch (err) {
      console.error('Error cargando grupos musculares:', err);
      setGruposMusculares([]);
      const fromEjercicios = ejercicios.flatMap((e) =>
        Array.isArray(e.musculosPrincipales) ? e.musculosPrincipales : []
      );
      const fallback = mergeMusculosUnicos([...musculosDisponiblesDefault, ...fromEjercicios]);
      setMusculosDisponibles(fallback.length ? fallback : musculosDisponiblesDefault);
    }
  };

  const handleAddMusculo = async (target: 'new' | 'edit') => {
    const nombre = normalizeMusculoNombre(nuevoMusculoNombre);
    if (!nombre) return;
    if (!sucursalActiva?.gimnasio_id) {
      alert('No hay gimnasio activo para crear el grupo muscular.');
      return;
    }

    const existe = musculosDisponibles.some((m) => musculoKey(m) === musculoKey(nombre));
    if (!existe) {
      setGuardandoNuevoMusculo(true);
      try {
        const nextOrden = (musculosDisponibles.length + 1) * 10;
        const { error: insertError } = await supabase
          .from('grupos_musculares')
          .insert({
            gimnasio_id: sucursalActiva.gimnasio_id,
            nombre,
            activo: true,
            orden: nextOrden,
          });
        if (insertError) throw insertError;
      } catch (err: any) {
        const msg = String(err?.message || '').toLowerCase();
        if (!msg.includes('duplicate key')) {
          console.error('Error creando grupo muscular:', err);
          alert('No se pudo crear el grupo muscular.');
          setGuardandoNuevoMusculo(false);
          return;
        }
      }
      setGuardandoNuevoMusculo(false);
      await fetchMusculosCatalogo();
    }

    if (target === 'new') {
      setNewExercise((prev) => ({
        ...prev,
        musculosPrincipales: prev.musculosPrincipales.some((m) => musculoKey(m) === musculoKey(nombre))
          ? prev.musculosPrincipales
          : [...prev.musculosPrincipales, nombre],
      }));
    } else {
      setSelectedExerciseForEdit((prev) => {
        if (!prev) return prev;
        const exists = prev.musculosPrincipales.some((m) => musculoKey(m) === musculoKey(nombre));
        return {
          ...prev,
          musculosPrincipales: exists ? prev.musculosPrincipales : [...prev.musculosPrincipales, nombre],
        };
      });
    }

    setNuevoMusculoNombre('');
    setShowNuevoMusculoInput(false);
  };

  const handleStartEditMusculo = (grupo: GrupoMuscularCatalogo) => {
    setEditingMusculoId(grupo.id);
    setEditingMusculoNombre(grupo.nombre);
  };

  const handleCancelEditMusculo = () => {
    setEditingMusculoId(null);
    setEditingMusculoNombre('');
  };

  const handleSaveEditMusculo = async (grupo: GrupoMuscularCatalogo) => {
    const nombre = normalizeMusculoNombre(editingMusculoNombre);
    if (!nombre || !sucursalActiva?.gimnasio_id) return;

    setProcesandoMusculoId(grupo.id);
    try {
      const { error: updateError } = await supabase
        .from('grupos_musculares')
        .update({ nombre })
        .eq('id', grupo.id)
        .eq('gimnasio_id', sucursalActiva.gimnasio_id);
      if (updateError) throw updateError;

      const prevNombre = grupo.nombre;
      setNewExercise((prev) => ({
        ...prev,
        musculosPrincipales: prev.musculosPrincipales.map((m) =>
          musculoKey(m) === musculoKey(prevNombre) ? nombre : m
        ),
      }));
      setSelectedExerciseForEdit((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          musculosPrincipales: prev.musculosPrincipales.map((m) =>
            musculoKey(m) === musculoKey(prevNombre) ? nombre : m
          ),
        };
      });

      await fetchMusculosCatalogo();
      setEditingMusculoId(null);
      setEditingMusculoNombre('');
    } catch (err: any) {
      console.error('Error actualizando grupo muscular:', err);
      alert('No se pudo actualizar el grupo muscular.');
    } finally {
      setProcesandoMusculoId(null);
    }
  };

  const handleDeactivateMusculo = async (grupo: GrupoMuscularCatalogo) => {
    if (!sucursalActiva?.gimnasio_id) return;
    const ok = confirm(`¿Desactivar "${grupo.nombre}"?`);
    if (!ok) return;

    setProcesandoMusculoId(grupo.id);
    try {
      const { error: updateError } = await supabase
        .from('grupos_musculares')
        .update({ activo: false })
        .eq('id', grupo.id)
        .eq('gimnasio_id', sucursalActiva.gimnasio_id);
      if (updateError) throw updateError;

      setNewExercise((prev) => ({
        ...prev,
        musculosPrincipales: prev.musculosPrincipales.filter(
          (m) => musculoKey(m) !== musculoKey(grupo.nombre)
        ),
      }));
      setSelectedExerciseForEdit((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          musculosPrincipales: prev.musculosPrincipales.filter(
            (m) => musculoKey(m) !== musculoKey(grupo.nombre)
          ),
        };
      });

      await fetchMusculosCatalogo();
      if (editingMusculoId === grupo.id) {
        setEditingMusculoId(null);
        setEditingMusculoNombre('');
      }
    } catch (err: any) {
      console.error('Error desactivando grupo muscular:', err);
      alert('No se pudo desactivar el grupo muscular.');
    } finally {
      setProcesandoMusculoId(null);
    }
  };

  const handleSaveEditExercise = async () => {
    if (!selectedExerciseForEdit || !selectedExerciseForEdit.nombre || !selectedExerciseForEdit.categoria || !selectedExerciseForEdit.dificultad) {
      return;
    }

    if (!sucursalActiva?.gimnasio_id) {
      alert('No hay gimnasio activo para guardar el ejercicio');
      return;
    }

    try {
      let uploadedVideoUrl = selectedExerciseForEdit.urlVideoSubido || null;
      let uploadedVideoPath = selectedExerciseForEdit.videoSubidoPath || null;

      if (selectedExerciseForEdit.tieneVideo && !isHttpUrl(selectedExerciseForEdit.urlVideo)) {
        alert('Debes ingresar una URL valida de video (http/https).');
        return;
      }

      if (selectedExerciseForEdit.tieneAnimacion && !isHttpUrl(selectedExerciseForEdit.urlGif)) {
        alert('Debes ingresar una URL valida para el GIF (http/https).');
        return;
      }

      if (selectedExerciseForEdit.tieneVideoSubido) {
        if (editExerciseVideoFile) {
          const uploaded = await uploadExerciseVideo(editExerciseVideoFile);
          uploadedVideoUrl = uploaded.publicUrl;
          uploadedVideoPath = uploaded.path;
        } else if (!uploadedVideoUrl || !uploadedVideoPath) {
          alert(`Debes subir un video (máximo ${MAX_EXERCISE_VIDEO_SIZE_MB} MB).`);
          return;
        }
      } else {
        uploadedVideoUrl = null;
        uploadedVideoPath = null;
      }

      const payload = {
        gimnasio_id: sucursalActiva.gimnasio_id,
        nombre: selectedExerciseForEdit.nombre,
        categoria: selectedExerciseForEdit.categoria,
        equipo: selectedExerciseForEdit.equipo,
        dificultad: selectedExerciseForEdit.dificultad,
        descripcion: selectedExerciseForEdit.descripcion || null,
        musculos_principales: selectedExerciseForEdit.musculosPrincipales || [],
        instrucciones: (selectedExerciseForEdit.instrucciones || []).map(String).map(s => s.trim()).filter(Boolean),
        consejos: (selectedExerciseForEdit.consejos || []).map(String).map(s => s.trim()).filter(Boolean),
        tiene_video: selectedExerciseForEdit.tieneVideo,
        tiene_animacion: selectedExerciseForEdit.tieneAnimacion,
        tiene_video_subido: selectedExerciseForEdit.tieneVideoSubido,
        url_video: selectedExerciseForEdit.tieneVideo ? (selectedExerciseForEdit.urlVideo || null) : null,
        url_gif: selectedExerciseForEdit.tieneAnimacion ? (selectedExerciseForEdit.urlGif || null) : null,
        url_video_subido: selectedExerciseForEdit.tieneVideoSubido ? uploadedVideoUrl : null,
        video_subido_path: selectedExerciseForEdit.tieneVideoSubido ? uploadedVideoPath : null,
        updated_at: new Date().toISOString(),
      };

      let { data, error } = await supabase
        .from('ejercicios')
        .update(payload)
        .eq('id', selectedExerciseForEdit.id)
        .select('*')
        .single();

      // Backward compat: if the DB hasn't been migrated yet, retry without new columns.
      if (error) {
        const msg = String((error as any)?.message || '');
        if (
          (
            msg.includes('instrucciones') ||
            msg.includes('consejos') ||
            msg.includes('tiene_video_subido') ||
            msg.includes('url_video_subido') ||
            msg.includes('video_subido_path')
          ) &&
          (msg.toLowerCase().includes('schema cache') || msg.toLowerCase().includes('could not find'))
        ) {
          const fallbackPayload = { ...payload } as any;
          delete fallbackPayload.instrucciones;
          delete fallbackPayload.consejos;
          delete fallbackPayload.tiene_video_subido;
          delete fallbackPayload.url_video_subido;
          delete fallbackPayload.video_subido_path;
          const retry = await supabase
            .from('ejercicios')
            .update(fallbackPayload)
            .eq('id', selectedExerciseForEdit.id)
            .select('*')
            .single();
          data = retry.data;
          error = retry.error as any;
        }
      }

      if (error) throw error;

      if (!selectedExerciseForEdit.tieneVideoSubido && originalExerciseForEdit?.videoSubidoPath) {
        await removeExerciseVideo(originalExerciseForEdit.videoSubidoPath);
      } else if (
        selectedExerciseForEdit.tieneVideoSubido &&
        editExerciseVideoFile &&
        originalExerciseForEdit?.videoSubidoPath &&
        originalExerciseForEdit.videoSubidoPath !== uploadedVideoPath
      ) {
        await removeExerciseVideo(originalExerciseForEdit.videoSubidoPath);
      }

      const updated = ejercicioFromRow(data as any);
      setEjercicios(prev => prev.map(e => (e.id === updated.id ? updated : e)));
      setIsEditExerciseModalOpen(false);
      setSelectedExerciseForEdit(null);
      setOriginalExerciseForEdit(null);
      setEditExerciseVideoFile(null);
    } catch (err: any) {
      console.error('Error actualizando ejercicio:', err);
      alert('Error al actualizar el ejercicio: ' + (err.message || ''));
    }
  };

  // Funciones para editar rutina
  const handleOpenEditRutinaModal = (rutina: Rutina) => {
    const rutinaSesiones = rutina.sesiones || sesionesEjemplo[rutina.id] || [];
    setSelectedRutinaForEdit(rutina);
    setEditTemplateGlobalScope(rutina.plantilla_global === true);
    setEditRutina({
      id: rutina.id,
      nombre: rutina.nombre,
      descripcion: rutina.descripcion,
      nivel: rutina.nivel,
      duracion: rutina.duracion,
      fechaInicio: rutina.fechaInicio || new Date().toISOString().split('T')[0],
      calentamientoGeneral: rutina.calentamientoGeneral || buildDefaultCalentamiento(),
      activa: rutina.activa
    });
    setEditSesiones(JSON.parse(JSON.stringify(rutinaSesiones)));
    setEditProgresionSemanal(rutina.progresionSemanal||{activa:false,semanas:[]});
    setEditSesionActiva(rutinaSesiones[0]?.id || 'A');
    setEditEjercicioSearchTerm('');
    setEditCategoriaFiltro('');
    setIsEditRutinaModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleCloseEditRutinaModal = () => {
    setIsEditRutinaModalOpen(false);
    setSelectedRutinaForEdit(null);
    setEditTemplateGlobalScope(false);
    notifyEmbedClose();
  };

  const handleAddEditSesion = () => {
    const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nextLetter = letras[editSesiones.length];
    if (editSesiones.length < 26) {
      setEditSesiones(prev => [...prev, {
        id: nextLetter,
        nombre: `Sesión ${nextLetter}`,
        ejercicios: []
      }]);
    }
  };

  const handleRemoveEditSesion = (sesionId: string) => {
    if (editSesiones.length > 1) {
      setEditSesiones(prev => prev.filter(s => s.id !== sesionId));
      if (editSesionActiva === sesionId) {
        setEditSesionActiva(editSesiones[0].id);
      }
    }
  };

  const handleRenameEditSesion = (sesionId: string, nuevoNombre: string) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === sesionId ? { ...s, nombre: nuevoNombre } : s
    ));
  };

  const handleAddEjercicioToEditRutina = (ejercicio: Ejercicio) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === editSesionActiva 
        ? {
            ...s,
            ejercicios: [...s.ejercicios, {
              ejercicioId: ejercicio.id,
              nombre: ejercicio.nombre,
              categoria: ejercicio.categoria,
              series: 3,
              repeticiones: 12,
              descanso: 60,
              peso: 0,
              rir: 2,
              tempo: '3-2-1',
            }]
          }
        : s
    ));
  };

  const handleRemoveEjercicioFromEditRutina = (ejercicioId: number) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === editSesionActiva 
        ? { ...s, ejercicios: s.ejercicios.filter(e => e.ejercicioId !== ejercicioId) }
        : s
    ));
  };

  const handleUpdateEjercicioEnEditRutina = (ejercicioId: number, field: keyof EjercicioEnRutina, value: number | string) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === editSesionActiva 
        ? {
            ...s,
            ejercicios: s.ejercicios.map(e => 
              e.ejercicioId === ejercicioId ? { ...e, [field]: value } : e
            )
          }
        : s
    ));
  };

  const handleMoveEditEjercicio = (index: number, direction: 'up' | 'down') => {
    const sesionActual = editSesiones.find(s => s.id === editSesionActiva);
    if (!sesionActual) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sesionActual.ejercicios.length) return;
    
    setEditSesiones(prev => prev.map(s => {
      if (s.id === editSesionActiva) {
        const newList = [...s.ejercicios];
        [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
        return { ...s, ejercicios: newList };
      }
      return s;
    }));
  };

  const handleSaveEditRutina = async () => {
    const totalEjercicios = editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0);
    if (!editRutina.nombre || !editRutina.nivel || totalEjercicios === 0) {
      return;
    }

    try {
      setError(null);

      const sesionesNormalizadas = normalizeSesionesForPersist(editSesiones);
      const ejerciciosData = {
        nivel: editRutina.nivel,
        duracion: editRutina.duracion,
        fecha_inicio: editRutina.fechaInicio || null,
        calentamiento_general: editRutina.calentamientoGeneral || buildDefaultCalentamiento(),
        sesiones: sesionesNormalizadas,
        progresion_semanal: editProgresionSemanal.activa ? normalizeProgresionForPersist(editProgresionSemanal) : {activa:false,semanas:[]},
      };

      const isEditingTemplate = selectedRutinaForEdit?.es_plantilla === true;

      const { error: updateError } = embeddedRequest?.recommendationId
        ? await supabase.rpc('actualizar_propuesta_rutina_ia', {
            p_recomendacion_id: embeddedRequest.recommendationId,
            p_nombre: editRutina.nombre,
            p_descripcion: editRutina.descripcion,
            p_ejercicios: ejerciciosData,
            p_dias_semana: sesionesNormalizadas.map(s => s.id),
            p_comentario: 'Ajustes realizados desde la revisión de la propuesta ACOMA.',
          })
        : await supabase
          .from('rutinas')
          .update({
            nombre: editRutina.nombre,
            descripcion: editRutina.descripcion,
            ejercicios: ejerciciosData,
            dias_semana: sesionesNormalizadas.map(s => s.id),
            activa: editRutina.activa,
            plantilla_global: isEditingTemplate ? editTemplateGlobalScope : false,
            ...(isEditingTemplate ? { usuario_id: null, usuarios_asignados: [] } : {}),
            updated_at: new Date().toISOString(),
          })
          .eq('id', editRutina.id);

      if (updateError) throw updateError;

      await fetchRutinas();
      setIsEditRutinaModalOpen(false);
      setSelectedRutinaForEdit(null);
      setEditTemplateGlobalScope(false);
      notifyEmbedClose(Boolean(embeddedRequest?.recommendationId));
    } catch (err: any) {
      console.error('Error actualizando rutina:', err);
      setError('Error al actualizar la rutina: ' + (err.message || ''));
      alert('Error al actualizar la rutina: ' + (err.message || ''));
    }
  };

  // Ejercicios disponibles para editar rutina
  const ejerciciosDisponiblesEdit = ejercicios.filter(ejercicio => {
    const matchSearch = ejercicio.nombre.toLowerCase().includes(editEjercicioSearchTerm.toLowerCase());
    const matchCategoria = !editCategoriaFiltro || ejercicio.categoria === editCategoriaFiltro;
    const sesionActual = editSesiones.find(s => s.id === editSesionActiva);
    const notAdded = !sesionActual?.ejercicios.some(e => e.ejercicioId === ejercicio.id);
    return matchSearch && matchCategoria && notAdded;
  });

  const editSesionActual = editSesiones.find(s => s.id === editSesionActiva);

  const handleNewExerciseMusculoToggle = (musculo: string) => {
    setNewExercise(prev => ({
      ...prev,
      musculosPrincipales: prev.musculosPrincipales.includes(musculo)
        ? prev.musculosPrincipales.filter(m => m !== musculo)
        : [...prev.musculosPrincipales, musculo]
    }));
  };

  const handleEditExerciseMusculoToggle = (musculo: string) => {
    setSelectedExerciseForEdit(prev => {
      if (!prev) return prev;
      const current = Array.isArray(prev.musculosPrincipales) ? prev.musculosPrincipales : [];
      const next = current.includes(musculo)
        ? current.filter(m => m !== musculo)
        : [...current, musculo];
      return { ...prev, musculosPrincipales: next };
    });
  };

  const handleToggleNewExerciseMedia = (mode: 'video' | 'gif' | 'upload', enabled: boolean) => {
    if (mode !== 'upload') {
      setNewExerciseVideoFile(null);
    }
    setNewExercise(prev => applyExclusiveMediaSelection(prev, mode, enabled));
  };

  const handleToggleEditExerciseMedia = (mode: 'video' | 'gif' | 'upload', enabled: boolean) => {
    if (mode !== 'upload') {
      setEditExerciseVideoFile(null);
    }
    setSelectedExerciseForEdit(prev => (prev ? applyExclusiveMediaSelection(prev, mode, enabled) : prev));
  };

  const handleNewExerciseVideoFileChange = async (file: File | null) => {
    if (!file) {
      setNewExerciseVideoFile(null);
      return;
    }
    validateExerciseVideoFile(file);
    setNewExerciseVideoFile(file);
    setNewExercise(prev => ({ ...prev, duracionVideoSegundos: null }));
  };

  const handleEditExerciseVideoFileChange = async (file: File | null) => {
    if (!file) {
      setEditExerciseVideoFile(null);
      return;
    }
    validateExerciseVideoFile(file);
    setEditExerciseVideoFile(file);
    setSelectedExerciseForEdit(prev => (prev ? { ...prev, duracionVideoSegundos: null } : prev));
  };

  const getRutinasAsignadasByExercise = (ejercicioId: number) => {
    const allRutinas = [...rutinas, ...plantillas];
    return allRutinas.filter((rutina) =>
      (rutina.sesiones || []).some((sesion) =>
        (sesion.ejercicios || []).some((ej) => Number(ej.ejercicioId) === Number(ejercicioId))
      )
    );
  };

  const handleDeleteExercise = async (ejercicio: Ejercicio) => {
    const rutinasAsignadas = getRutinasAsignadasByExercise(ejercicio.id);
    if (rutinasAsignadas.length > 0) {
      const ejemplos = rutinasAsignadas.slice(0, 3).map((r) => r.nombre).join(', ');
      const extra = rutinasAsignadas.length > 3 ? ` y ${rutinasAsignadas.length - 3} más` : '';
      alert(`No puedes eliminar este ejercicio porque está asignado en rutinas/plantillas (${ejemplos}${extra}).`);
      return;
    }

    const ok = confirm(`¿Eliminar el ejercicio "${ejercicio.nombre}"?\n\nEsta acción no se puede deshacer.`);
    if (!ok) return;

    try {
      setDeletingExerciseId(ejercicio.id);
      const { error: deleteError } = await supabase
        .from('ejercicios')
        .delete()
        .eq('id', ejercicio.id)
        .eq('gimnasio_id', sucursalActiva?.gimnasio_id || '');

      if (deleteError) throw deleteError;

      setEjercicios((prev) => prev.filter((e) => e.id !== ejercicio.id));

      if (selectedExerciseForDetail?.id === ejercicio.id) {
        handleCloseExerciseDetailModal();
      }

      if (selectedExerciseForEdit?.id === ejercicio.id) {
        handleCloseEditExerciseModal();
      }
    } catch (err: any) {
      console.error('Error eliminando ejercicio:', err);
      alert('No se pudo eliminar el ejercicio: ' + (err?.message || ''));
    } finally {
      setDeletingExerciseId(null);
    }
  };

  const handleSaveExercise = async () => {
    if (!newExercise.nombre || !newExercise.categoria || !newExercise.dificultad) {
      return;
    }

    if (!sucursalActiva?.gimnasio_id) {
      alert('No hay gimnasio activo para guardar el ejercicio');
      return;
    }

    try {
      let uploadedVideoUrl: string | null = null;
      let uploadedVideoPath: string | null = null;

      if (newExercise.tieneVideo && !isHttpUrl(newExercise.urlVideo)) {
        alert('Debes ingresar una URL valida de video (http/https).');
        return;
      }

      if (newExercise.tieneAnimacion && !isHttpUrl(newExercise.urlGif)) {
        alert('Debes ingresar una URL valida para el GIF (http/https).');
        return;
      }

      if (newExercise.tieneVideoSubido) {
        if (!newExerciseVideoFile) {
          alert(`Debes subir un video (máximo ${MAX_EXERCISE_VIDEO_SIZE_MB} MB).`);
          return;
        }
        const uploaded = await uploadExerciseVideo(newExerciseVideoFile);
        uploadedVideoUrl = uploaded.publicUrl;
        uploadedVideoPath = uploaded.path;
      }

      const payload = {
        gimnasio_id: sucursalActiva.gimnasio_id,
        nombre: newExercise.nombre,
        categoria: newExercise.categoria,
        equipo: newExercise.equipo,
        dificultad: newExercise.dificultad,
        descripcion: newExercise.descripcion || null,
        musculos_principales: newExercise.musculosPrincipales || [],
        instrucciones: (newExercise.instrucciones || []).map(String).map(s => s.trim()).filter(Boolean),
        consejos: (newExercise.consejos || []).map(String).map(s => s.trim()).filter(Boolean),
        tiene_video: newExercise.tieneVideo,
        tiene_animacion: newExercise.tieneAnimacion,
        tiene_video_subido: newExercise.tieneVideoSubido,
        url_video: newExercise.tieneVideo ? (newExercise.urlVideo || null) : null,
        url_gif: newExercise.tieneAnimacion ? (newExercise.urlGif || null) : null,
        url_video_subido: newExercise.tieneVideoSubido ? uploadedVideoUrl : null,
        video_subido_path: newExercise.tieneVideoSubido ? uploadedVideoPath : null,
        alcance: isCoach ? 'privado' : 'general',
        created_by_admin_id: profile?.adminId || null,
      };

      let { data, error } = await supabase
        .from('ejercicios')
        .insert(payload)
        .select('*')
        .single();

      // Backward compat: if the DB hasn't been migrated yet, retry without new columns.
      if (error) {
        const msg = String((error as any)?.message || '');
        if (
          (
            msg.includes('instrucciones') ||
            msg.includes('consejos') ||
            msg.includes('tiene_video_subido') ||
            msg.includes('url_video_subido') ||
            msg.includes('video_subido_path')
          ) &&
          (msg.toLowerCase().includes('schema cache') || msg.toLowerCase().includes('could not find'))
        ) {
          const fallbackPayload = { ...payload } as any;
          delete fallbackPayload.instrucciones;
          delete fallbackPayload.consejos;
          delete fallbackPayload.tiene_video_subido;
          delete fallbackPayload.url_video_subido;
          delete fallbackPayload.video_subido_path;
          const retry = await supabase
            .from('ejercicios')
            .insert(fallbackPayload)
            .select('*')
            .single();
          data = retry.data;
          error = retry.error as any;
        }
      }

      if (error) throw error;

      const inserted = ejercicioFromRow(data as any);
      setEjercicios(prev => [inserted, ...prev]);
      setIsNewExerciseModalOpen(false);
      setNewExerciseVideoFile(null);
    } catch (err: any) {
      console.error('Error creando ejercicio:', err);
      alert('Error al crear el ejercicio: ' + (err.message || ''));
    }
  };

  const handleAddSesion = () => {
    const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nextLetter = letras[sesiones.length];
    if (sesiones.length < 26) {
      setSesiones(prev => [...prev, {
        id: nextLetter,
        nombre: `Sesión ${nextLetter}`,
        ejercicios: []
      }]);
    }
  };

  const handleRemoveSesion = (sesionId: string) => {
    if (sesiones.length > 1) {
      setSesiones(prev => prev.filter(s => s.id !== sesionId));
      if (sesionActiva === sesionId) {
        setSesionActiva(sesiones[0].id);
      }
    }
  };

  const handleRenameSesion = (sesionId: string, nuevoNombre: string) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionId ? { ...s, nombre: nuevoNombre } : s
    ));
  };

  const handleAddEjercicioToRutina = (ejercicio: Ejercicio) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionActiva 
        ? {
            ...s,
            ejercicios: [...s.ejercicios, {
              ejercicioId: ejercicio.id,
              nombre: ejercicio.nombre,
              categoria: ejercicio.categoria,
              series: 3,
              repeticiones: 12,
              descanso: 60,
              peso: 0,
              rir: 2,
              tempo: '3-2-1',
            }]
          }
        : s
    ));
  };

  const handleRemoveEjercicioFromRutina = (ejercicioId: number) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionActiva 
        ? { ...s, ejercicios: s.ejercicios.filter(e => e.ejercicioId !== ejercicioId) }
        : s
    ));
  };

  const handleUpdateEjercicioEnRutina = (ejercicioId: number, field: keyof EjercicioEnRutina, value: number | string) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionActiva 
        ? {
            ...s,
            ejercicios: s.ejercicios.map(e => 
              e.ejercicioId === ejercicioId ? { ...e, [field]: value } : e
            )
          }
        : s
    ));
  };

  const handleMoveEjercicio = (index: number, direction: 'up' | 'down') => {
    const sesionActual = sesiones.find(s => s.id === sesionActiva);
    if (!sesionActual) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sesionActual.ejercicios.length) return;
    
    setSesiones(prev => prev.map(s => {
      if (s.id === sesionActiva) {
        const newList = [...s.ejercicios];
        [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
        return { ...s, ejercicios: newList };
      }
      return s;
    }));
  };

  const handleSaveRutina = async () => {
    const totalEjercicios = sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0);
    if (!newRutina.nombre || !newRutina.nivel || totalEjercicios === 0) {
      return;
    }

    if (!sucursalActiva?.id) {
      alert('Debes seleccionar una sucursal para crear rutinas');
      return;
    }

    try {
      const sesionesNormalizadas = normalizeSesionesForPersist(sesiones);
      const ejerciciosData = {
        nivel: newRutina.nivel,
        duracion: newRutina.duracion,
        fecha_inicio: newRutina.fechaInicio || null,
        calentamiento_general: newRutina.calentamientoGeneral || buildDefaultCalentamiento(),
        sesiones: sesionesNormalizadas,
        progresion_semanal: progresionSemanal.activa ? normalizeProgresionForPersist(progresionSemanal) : {activa:false,semanas:[]},
      };

      const { error } = await supabase
        .from('rutinas')
        .insert({
          usuario_id: null,
          gimnasio_id: sucursalActiva.gimnasio_id,
          nombre: newRutina.nombre,
          descripcion: newRutina.descripcion,
          ejercicios: ejerciciosData,
          dias_semana: sesionesNormalizadas.map(s => s.id),
          activa: newRutina.activa,
          es_plantilla: isTemplateMode,
          plantilla_global: isTemplateMode ? templateGlobalScope : false,
          creada_por_usuario: false,
          usuarios_asignados: [],
          created_by_admin_id: profile?.adminId || null,
          rutina_origen_id: isExportingTemplate ? exportTemplateSourceId : null,
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      // Refresh list
      await fetchRutinas();
      setIsNewRutinaModalOpen(false);
      if (isExportingTemplate) {
        const nombreRutinaCreada = newRutina.nombre.trim();
        setActiveTab('rutinas');
        setSearchTerm('');
        setExportSuccessName(nombreRutinaCreada);
        setTimeout(() => setExportSuccessName(null), 5000);
      }
      setIsExportingTemplate(false);
      setExportTemplateSourceId(null);
    } catch (err: any) {
      console.error('Error saving rutina:', err);
      alert('Error al guardar la rutina: ' + err.message);
    }
  };

  const handleOpenAssignModal = (rutina: Rutina) => {
    setSelectedFamiliaForAssign(null);
    setFamiliaAlreadyAssignedIds([]);
    setSelectedRutinaForAssign(rutina);
    // Pre-seleccionar usuarios ya asignados
    setSelectedUserIds(rutina.usuarios_asignados || []);
    setSelectedCoachId(isCoach && profile?.adminId ? profile.adminId : '');
    setUserSearchTerm('');
    setIsAssignModalOpen(true);
  };

  const handleOpenFamiliaAssignModal = async (familia: FamiliaRutina) => {
    setSelectedRutinaForAssign(null);
    setSelectedFamiliaForAssign(familia);
    setSelectedUserIds([]);
    setSelectedCoachId(isCoach && profile?.adminId ? profile.adminId : '');
    setFamiliaAlreadyAssignedIds([]);
    setUserSearchTerm('');
    setIsAssignModalOpen(true);
    const { data, error } = await supabase.from('usuario_familia_rutina')
      .select('usuario_id').eq('familia_id', familia.id).eq('estado', 'activa');
    if (error) {
      setError('No se pudieron consultar los usuarios asignados a esta familia.');
      return;
    }
    const ids = (data || []).map((row: any) => String(row.usuario_id));
    setFamiliaAlreadyAssignedIds(ids);
    setSelectedUserIds(ids);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedRutinaForAssign(null);
    setSelectedFamiliaForAssign(null);
    setFamiliaAlreadyAssignedIds([]);
    setSelectedUserIds([]);
    setSelectedCoachId('');
    setUserSearchTerm('');
    setInitialEvaluationsByUser({});
    setExpandedEvaluationUserId(null);
  };

  const handleToggleUserSelection = (userId: string) => {
    if (familiaAlreadyAssignedIds.includes(userId)) return;
    const usuario = usuarios.find((item) => item.id === userId);
    if (!usuario || usuario.estado.toLocaleLowerCase('es-CL') !== 'activo') return;
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleToggleAllUsers = () => {
    const filteredUsers = usuarios.filter(usuario =>
      usuario.estado.toLocaleLowerCase('es-CL') === 'activo' &&
      (usuario.nombre.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      usuario.email.toLowerCase().includes(userSearchTerm.toLowerCase()))
    );
    
    const filteredUserIds = filteredUsers.map(u => u.id).filter(id => !familiaAlreadyAssignedIds.includes(id));
    
    // Si todos los usuarios filtrados están seleccionados, deseleccionar todos
    const allSelected = filteredUserIds.every(id => selectedUserIds.includes(id));
    
    if (allSelected) {
      // Remover solo los IDs de usuarios filtrados
      setSelectedUserIds(prev => prev.filter(id => !filteredUserIds.includes(id)));
    } else {
      // Agregar los IDs de usuarios filtrados que no están seleccionados
      setSelectedUserIds(prev => {
        const newIds = filteredUserIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handleAssignRutina = async () => {
    if (!selectedCoachId) {
      setError('Debes seleccionar el coach responsable de la rutina.');
      return;
    }
    if (selectedFamiliaForAssign) {
      const nuevosIds = selectedUserIds.filter(id => !familiaAlreadyAssignedIds.includes(id));
      if (nuevosIds.length === 0) return;
      try {
        setError(null);
        const { error: assignError } = await supabase.rpc('asignar_familia_rutina_masivo', {
          p_usuario_ids: nuevosIds,
          p_familia_id: selectedFamiliaForAssign.id,
          p_coach_admin_id: selectedCoachId,
        });
        if (assignError) throw assignError;
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 3000);
        handleCloseAssignModal();
      } catch (err: any) {
        console.error('Error asignando familia:', err);
        setError(err?.message || 'No se pudo asignar la familia de rutinas.');
      }
      return;
    }
    if (selectedUserIds.length === 0 || !selectedRutinaForAssign) return;

    try {
      setError(null);

      const selectedAsUuid = selectedUserIds.map((id) => String(id));
      const { error: assignError } = await supabase.rpc('asignar_rutina_por_copia', {
        p_rutina_origen_id: selectedRutinaForAssign.id,
        p_usuario_ids: selectedAsUuid,
        p_coach_admin_id: selectedCoachId,
      });
      if (assignError) throw assignError;

      // Refrescamos para mantener consistencia en contadores y listas.
      await fetchRutinas();

      // Mostrar notificación de éxito
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);

      // Cerrar modal
      handleCloseAssignModal();
    } catch (err: any) {
      console.error('Error asignando rutina:', err);
      setError('Error al asignar la rutina: ' + err.message);
    }
  };

  const activeUsersForAssign = usuarios.filter(
    (usuario) => usuario.estado.toLocaleLowerCase('es-CL') === 'activo'
  );
  const filteredUsersForAssign = activeUsersForAssign.filter(usuario =>
    usuario.nombre.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    usuario.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const allFilteredUsersSelected = filteredUsersForAssign.length > 0 && 
    filteredUsersForAssign.filter(u => !familiaAlreadyAssignedIds.includes(u.id)).every(u => selectedUserIds.includes(u.id));
  const newFamilyAssignmentsCount = selectedUserIds.filter(id => !familiaAlreadyAssignedIds.includes(id)).length;

  const sesionActual = sesiones.find(s => s.id === sesionActiva);

  // Obtener sesiones de la rutina seleccionada para detalles
  const getRutinaSesiones = (rutinaId: number): Sesion[] => {
    if (selectedRutinaForDetail?.id === rutinaId && selectedRutinaForDetail.sesiones?.length) {
      return selectedRutinaForDetail.sesiones;
    }

    const rutina = rutinas.find(r => r.id === rutinaId);
    if (rutina?.sesiones?.length) return rutina.sesiones;

    const plantilla = plantillas.find(p => p.id === rutinaId);
    if (plantilla?.sesiones?.length) return plantilla.sesiones;

    return sesionesEjemplo[rutinaId] || [];
  };

  type EjercicioRow = {
    id: number;
    gimnasio_id: string;
    nombre: string;
    categoria: string;
    equipo: string;
    dificultad: string;
    descripcion: string | null;
    musculos_principales: string[] | null;
    instrucciones: string[] | null;
    consejos: string[] | null;
    tiene_video: boolean | null;
    tiene_animacion: boolean | null;
    tiene_video_subido: boolean | null;
    url_video: string | null;
    url_gif: string | null;
    url_video_subido: string | null;
    video_subido_path: string | null;
    duracion_video_segundos: number | null;
    alcance: 'general' | 'privado' | null;
    created_by_admin_id: string | null;
    created_at: string;
    updated_at: string;
  };

  const ejercicioFromRow = (row: EjercicioRow): Ejercicio => ({
    id: row.id,
    nombre: row.nombre,
    categoria: row.categoria,
    equipo: row.equipo || '',
    dificultad: row.dificultad,
    descripcion: row.descripcion || '',
    musculosPrincipales: row.musculos_principales || [],
    instrucciones: row.instrucciones || [],
    consejos: row.consejos || [],
    tieneVideo: row.tiene_video ?? false,
    tieneAnimacion: row.tiene_animacion ?? false,
    tieneVideoSubido: row.tiene_video_subido ?? false,
    urlVideo: row.url_video || '',
    urlGif: row.url_gif || '',
    urlVideoSubido: row.url_video_subido || '',
    videoSubidoPath: row.video_subido_path || '',
    duracionVideoSegundos: row.duracion_video_segundos ?? null,
    alcance: row.alcance === 'privado' ? 'privado' : 'general',
    createdByAdminId: row.created_by_admin_id || null,
  });

  const isCoach = profile?.role === 'staff' && ['coach_interno', 'coach_externo'].includes(profile?.tipoStaff || '');
  const canManageExercise = (exercise: Ejercicio) =>
    !isCoach || (exercise.alcance === 'privado' && exercise.createdByAdminId === profile?.adminId);

  const fetchEjercicios = async () => {
    if (!sucursalActiva?.gimnasio_id) {
      setEjercicios([]);
      setErrorEjercicios(null);
      setLoadingEjercicios(false);
      return;
    }

    setLoadingEjercicios(true);
    setErrorEjercicios(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('ejercicios')
        .select('*')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .order('nombre', { ascending: true });

      if (fetchError) throw fetchError;

      setEjercicios((data || []).map((r: any) => ejercicioFromRow(r as EjercicioRow)));
    } catch (err: any) {
      console.error('Error fetching ejercicios:', err);
      setEjercicios([]);
      setErrorEjercicios(err.message || 'Error al cargar ejercicios');
    } finally {
      setLoadingEjercicios(false);
    }
  };

  // Fetch rutinas from Supabase
  useEffect(() => {
    fetchRutinas();
  }, [sucursalActiva?.id]);

  const fetchFamilias = async () => {
    const gimnasioId = sucursalActiva?.gimnasio_id;
    if (!gimnasioId) return setFamilias([]);
    try {
      setFamiliasLoading(true);
      const { data, error } = await supabase
        .from('rutina_familias')
        .select('id,nombre,descripcion,activa,avance_automatico,dias_aprobacion,rutina_familia_etapas(id,rutina_id,orden,duracion_dias,sesiones_minimas,rutina:rutinas(id,nombre))')
        .eq('gimnasio_id', gimnasioId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFamilias(((data || []) as unknown as FamiliaRutina[]).map((familia) => ({
        ...familia,
        rutina_familia_etapas: [...(familia.rutina_familia_etapas || [])].sort((a, b) => a.orden - b.orden),
      })));
    } catch (err: any) {
      console.error('Error cargando familias:', err);
      setError(err?.message || 'No se pudieron cargar las familias de rutinas');
    } finally {
      setFamiliasLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilias();
  }, [sucursalActiva?.gimnasio_id]);

  const crearFamilia = async () => {
    const gimnasioId = sucursalActiva?.gimnasio_id;
    if (!gimnasioId || !familiaNombre.trim()) return;
    try {
      setFamiliaSaving(true);
      const nivelesValidos = familiaNiveles.filter((nivel) => nivel.rutinaId);
      if (nivelesValidos.length === 0) throw new Error('Agrega al menos un nivel con su rutina.');
      if (new Set(nivelesValidos.map((nivel) => nivel.rutinaId)).size !== nivelesValidos.length) throw new Error('No puedes repetir la misma rutina en dos niveles.');
      const { data: familiaCreada, error } = await supabase.from('rutina_familias').insert({
        gimnasio_id: gimnasioId,
        nombre: familiaNombre.trim(),
        descripcion: familiaDescripcion.trim() || null,
        dias_aprobacion: familiaDiasAprobacion,
        avance_automatico: familiaAvanceAutomatico,
        created_by_admin_id: profile?.adminId || null,
      }).select('id').single();
      if (error) throw error;
      const { error: nivelesError } = await supabase.from('rutina_familia_etapas').insert(nivelesValidos.map((nivel, index) => ({
        familia_id: familiaCreada.id, rutina_id: nivel.rutinaId, orden: index + 1,
        duracion_dias: nivel.duracionDias, sesiones_minimas: nivel.sesionesMinimas.trim() ? Number(nivel.sesionesMinimas) : null,
      })));
      if (nivelesError) throw nivelesError;
      setFamiliaNombre(''); setFamiliaDescripcion(''); setFamiliaDiasAprobacion(5); setFamiliaAvanceAutomatico(true); setFamiliaNiveles([{ rutinaId: '', duracionDias: 30, sesionesMinimas: '' }]); setShowFamiliaForm(false);
      await fetchFamilias();
    } catch (err: any) { alert(err?.message || 'No se pudo crear la familia'); }
    finally { setFamiliaSaving(false); }
  };

  const abrirEdicionFamilia = (familia: FamiliaRutina) => {
    setFamiliaEditandoId(familia.id);
    setFamiliaEditDraft({
      nombre: familia.nombre,
      descripcion: familia.descripcion || '',
      activa: familia.activa,
      avanceAutomatico: familia.avance_automatico,
      diasAprobacion: familia.dias_aprobacion,
    });
    setFamiliaEditandoEtapaId(null);
  };

  const guardarEdicionFamilia = async (familia: FamiliaRutina) => {
    if (!familiaEditDraft?.nombre.trim()) return;
    try {
      setFamiliaSaving(true);
      const { error } = await supabase.from('rutina_familias').update({
        nombre: familiaEditDraft.nombre.trim(),
        descripcion: familiaEditDraft.descripcion.trim() || null,
        activa: familiaEditDraft.activa,
        avance_automatico: familiaEditDraft.avanceAutomatico,
        dias_aprobacion: familiaEditDraft.diasAprobacion,
        updated_at: new Date().toISOString(),
      }).eq('id', familia.id);
      if (error) throw error;
      setFamiliaEditandoId(null);
      setFamiliaEditDraft(null);
      await fetchFamilias();
    } catch (err: any) {
      alert(err?.message || 'No se pudo actualizar la familia.');
    } finally {
      setFamiliaSaving(false);
    }
  };

  const abrirEdicionEtapaFamilia = (etapa: FamiliaRutina['rutina_familia_etapas'][number]) => {
    setFamiliaEditandoEtapaId(etapa.id);
    setFamiliaEtapaDraft({
      rutinaId: String(etapa.rutina_id),
      duracionDias: etapa.duracion_dias,
      sesionesMinimas: etapa.sesiones_minimas == null ? '' : String(etapa.sesiones_minimas),
    });
  };

  const abrirRutinaDeFamilia = (rutinaId: string) => {
    const rutina = rutinas.find((item) => String(item.id) === String(rutinaId));
    if (!rutina) {
      alert('No se pudo cargar la rutina de esta etapa. Actualiza la página e inténtalo nuevamente.');
      return;
    }
    void handleOpenDetailModal(rutina);
  };

  const guardarEdicionEtapaFamilia = async (familia: FamiliaRutina, etapaId: string) => {
    if (!familiaEtapaDraft.rutinaId) return alert('Selecciona una rutina para esta etapa.');
    const duplicada = familia.rutina_familia_etapas.some((etapa) =>
      etapa.id !== etapaId && String(etapa.rutina_id) === familiaEtapaDraft.rutinaId
    );
    if (duplicada) return alert('No puedes repetir la misma rutina en dos etapas.');
    try {
      setFamiliaSaving(true);
      const { error } = await supabase.from('rutina_familia_etapas').update({
        rutina_id: familiaEtapaDraft.rutinaId,
        duracion_dias: familiaEtapaDraft.duracionDias,
        sesiones_minimas: familiaEtapaDraft.sesionesMinimas.trim() ? Number(familiaEtapaDraft.sesionesMinimas) : null,
      }).eq('id', etapaId);
      if (error) throw error;
      setFamiliaEditandoEtapaId(null);
      await fetchFamilias();
    } catch (err: any) {
      alert(err?.message || 'No se pudo actualizar la etapa.');
    } finally {
      setFamiliaSaving(false);
    }
  };

  const agregarEtapaFamilia = async (familia: FamiliaRutina) => {
    if (!nuevoNivelDraft.rutinaId) return alert('Selecciona una rutina para el nuevo nivel.');
    const { error } = await supabase.from('rutina_familia_etapas').insert({
      familia_id: familia.id, rutina_id: nuevoNivelDraft.rutinaId, orden: familia.rutina_familia_etapas.length + 1,
      duracion_dias: nuevoNivelDraft.duracionDias, sesiones_minimas: nuevoNivelDraft.sesionesMinimas.trim() ? Number(nuevoNivelDraft.sesionesMinimas) : null,
    });
    if (error) return alert(error.message);
    setFamiliaEditandoNivelesId(null);
    setNuevoNivelDraft({ rutinaId: '', duracionDias: 30, sesionesMinimas: '' });
    await fetchFamilias();
  };

  const eliminarEtapaFamilia = async (etapaId: string) => {
    if (!window.confirm('¿Quitar esta etapa de la familia?')) return;
    const { error } = await supabase.from('rutina_familia_etapas').delete().eq('id', etapaId);
    if (error) return alert(error.message);
    await fetchFamilias();
  };

  const eliminarFamilia = async (familia: FamiliaRutina) => {
    const { count, error: countError } = await supabase
      .from('usuario_familia_rutina')
      .select('id', { count: 'exact', head: true })
      .eq('familia_id', familia.id);
    if (countError) return alert(`No se pudo validar la familia: ${countError.message}`);
    if ((count || 0) > 0) {
      return alert('No se puede eliminar porque esta familia tiene historial de socios asignados. Puedes dejarla inactiva para impedir nuevas asignaciones.');
    }
    if (!window.confirm(`¿Eliminar la familia “${familia.nombre}” y todos sus niveles?`)) return;
    const { error } = await supabase.from('rutina_familias').delete().eq('id', familia.id);
    if (error) return alert(error.message);
    if (familiaEditandoNivelesId === familia.id) setFamiliaEditandoNivelesId(null);
    await fetchFamilias();
  };

  useEffect(() => {
    fetchEjercicios();
  }, [sucursalActiva?.gimnasio_id]);

  useEffect(() => {
    fetchMusculosCatalogo();
  }, [sucursalActiva?.gimnasio_id]);

  useEffect(() => {
    if (!ejercicios.length) return;
    const fromEjercicios = ejercicios.flatMap((e) =>
      Array.isArray(e.musculosPrincipales) ? e.musculosPrincipales : []
    );
    if (!fromEjercicios.length) return;
    setMusculosDisponibles((prev) => mergeMusculosUnicos([...prev, ...fromEjercicios]));
  }, [ejercicios]);

  // Fetch usuarios from Supabase
  const fetchUsuarios = async () => {
    if (!sucursalActiva?.id) {
      console.log('No hay sucursal activa');
      setUsuarios([]);
      setLoadingUsuarios(false);
      return;
    }

    setLoadingUsuarios(true);
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nombre, email, tipo_membresia, estado')
        .eq('sucursal_id', sucursalActiva.id);

      if (error) throw error;

      const transformedUsuarios = (data || []).map((usuario: any) => {
        const initials = usuario.nombre
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);

        return {
          id: usuario.id,
          nombre: usuario.nombre,
          email: usuario.email,
          avatar: initials,
          plan: usuario.tipo_membresia || 'Sin plan',
          estado: usuario.estado || 'Activo'
        };
      });

      setUsuarios(transformedUsuarios);
    } catch (err: any) {
      console.error('Error fetching usuarios:', err);
      setUsuarios([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  // Fetch rutinas when assign modal opens
  useEffect(() => {
    if (isAssignModalOpen) {
      fetchUsuarios();
    }
  }, [isAssignModalOpen, sucursalActiva]);

  useEffect(() => {
    if (!isAssignModalOpen || !sucursalActiva?.gimnasio_id) return;
    let active = true;
    const loadCoaches = async () => {
      setLoadingCoachesAsignacion(true);
      const { data, error: coachesError } = await supabase
        .from('usuarios_admin')
        .select('id,nombre,email,tipo_staff')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .eq('role', 'staff')
        .in('tipo_staff', ['coach_interno', 'coach_externo'])
        .eq('activo', true)
        .order('nombre', { ascending: true });
      if (!active) return;
      setLoadingCoachesAsignacion(false);
      if (coachesError) {
        console.error('Error cargando coaches para asignación:', coachesError);
        setCoachesAsignacion([]);
        return;
      }
      const coaches = (data || []) as CoachAsignacion[];
      setCoachesAsignacion(coaches);
      setSelectedCoachId((current) => {
        if (current && coaches.some((coach) => coach.id === current)) return current;
        if (isCoach && profile?.adminId && coaches.some((coach) => coach.id === profile.adminId)) return profile.adminId;
        return '';
      });
    };
    void loadCoaches();
    return () => { active = false; };
  }, [isAssignModalOpen, sucursalActiva?.gimnasio_id, isCoach, profile?.adminId]);

  useEffect(() => {
    if (!isAssignModalOpen || !sucursalActiva?.gimnasio_id) return;
    let active = true;
    const loadInitialEvaluationSummaries = async () => {
      const { data, error: evaluationsError } = await supabase
        .from('evaluaciones_iniciales')
        .select('id,usuario_id,fecha_evaluacion,estado,nivel_entrenamiento,experiencia_gimnasio,frecuencia_semanal,sentadilla_puntaje,bisagra_puntaje,bisagra_dolor,tobillo_derecho_cm,tobillo_derecho_nt,tobillo_izquierdo_cm,tobillo_izquierdo_nt,tobillo_diferencia_cm,hombro_derecho_puntaje,hombro_izquierdo_puntaje,hombro_dolor_derecho,hombro_dolor_izquierdo,dolor_sintoma,dolor_detalle,observaciones_generales')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .neq('estado', 'borrador')
        .order('fecha_evaluacion', { ascending: false })
        .order('created_at', { ascending: false });
      if (!active) return;
      if (evaluationsError) {
        console.error('Error cargando evaluaciones para asignación:', evaluationsError);
        setInitialEvaluationsByUser({});
        return;
      }
      const latestByUser: Record<string, EvaluacionInicialResumen> = {};
      for (const evaluation of (data || []) as EvaluacionInicialResumen[]) {
        if (!latestByUser[evaluation.usuario_id]) latestByUser[evaluation.usuario_id] = evaluation;
      }
      setInitialEvaluationsByUser(latestByUser);
    };
    void loadInitialEvaluationSummaries();
    return () => { active = false; };
  }, [isAssignModalOpen, sucursalActiva?.gimnasio_id]);

  const fetchRutinas = async () => {
    if (!sucursalActiva?.id) {
      setRutinas([]);
      setPlantillas([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const mapRutina = (rutina: any): Rutina => {
        const ejerciciosData = rutina.ejercicios || { sesiones: [] };
        const sesiones = ejerciciosData.sesiones || [];
        const totalEjercicios = sesiones.reduce((sum: number, s: any) => sum + (s.ejercicios?.length || 0), 0);
        const usuariosAsignados = Array.isArray(rutina.usuarios_asignados) ? rutina.usuarios_asignados : [];

        return {
          id: rutina.id,
          nombre: rutina.nombre,
          descripcion: rutina.descripcion || '',
          nivel: ejerciciosData.nivel || 'Intermedio',
          duracion: ejerciciosData.duracion || 45,
          fechaInicio: ejerciciosData.fecha_inicio || '',
          calentamientoGeneral: ejerciciosData.calentamiento_general || buildDefaultCalentamiento(),
          ejercicios: totalEjercicios,
          usuariosAsignados: usuariosAsignados.length,
          activa: rutina.activa,
          es_plantilla: rutina.es_plantilla === true,
          plantilla_global: rutina.plantilla_global === true,
          creada_por_usuario: rutina.creada_por_usuario === true,
          es_copia_usuario: rutina.es_copia_usuario === true,
          created_by_admin_id: rutina.created_by_admin_id || null,
          rutina_origen_id: rutina.rutina_origen_id || null,
          sesiones: sesiones,
          progresionSemanal: ejerciciosData.progresion_semanal || { activa: false, semanas: [] },
          usuarios_asignados: usuariosAsignados
        };
      };

      // Las rutinas administrativas pertenecen al gimnasio y pueden existir sin
      // usuario hasta que sean asignadas. Filtrarlas por usuario_id hacía que una
      // rutina recién exportada desde una plantilla desapareciera del listado.
      const { data, error: fetchError } = await supabase
        .from('rutinas')
        .select('*')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .or('es_plantilla.is.null,es_plantilla.eq.false')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const transformedRutinas: Rutina[] = (data || []).map(mapRutina);
      setRutinas(transformedRutinas);

      const plantillasQuery = supabase
        .from('rutinas')
        .select('*')
        .eq('es_plantilla', true)
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .order('created_at', { ascending: false });

      const { data: dataPlantillas, error: fetchPlantillasError } = await plantillasQuery;

      if (fetchPlantillasError) throw fetchPlantillasError;

      const transformedPlantillas: Rutina[] = (dataPlantillas || []).map(mapRutina);
      setPlantillas(transformedPlantillas);
    } catch (err: any) {
      console.error('Error fetching rutinas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedCount = (rutina: Rutina) => {
    if (Array.isArray(rutina.usuarios_asignados)) {
      return rutina.usuarios_asignados.map(String).map(s => s.trim()).filter(Boolean).length;
    }
    return rutina.usuariosAsignados || 0;
  };

  const handleDeleteRutina = async (rutina: Rutina) => {
    const assignedCount = getAssignedCount(rutina);
    if (assignedCount > 0) {
      alert('No puedes eliminar esta rutina porque tiene usuarios asignados. Primero desasigna a todos los usuarios.');
      return;
    }

    const ok = confirm(`¿Eliminar la rutina "${rutina.nombre}"?\n\nEsta acción no se puede deshacer.`);
    if (!ok) return;

    try {
      setDeletingRutinaId(rutina.id);
      const { error: deleteError } = await supabase
        .from('rutinas')
        .delete()
        .eq('id', rutina.id);

      if (deleteError) throw deleteError;

      setRutinas(prev => prev.filter(r => r.id !== rutina.id));

      if (selectedRutinaForDetail?.id === rutina.id) {
        handleCloseDetailModal();
      }
    } catch (err: any) {
      console.error('Error eliminando rutina:', err);
      const msg = String(err?.message || '');
      if (msg.toLowerCase().includes('usuarios asignados')) {
        alert('No puedes eliminar esta rutina porque tiene usuarios asignados. Primero desasigna a todos los usuarios.');
      } else {
        alert('Error al eliminar la rutina: ' + (err?.message || ''));
      }
    } finally {
      setDeletingRutinaId(null);
    }
  };

  // Add function to fetch usuarios asignados
  const fetchUsuariosAsignados = async (usuarioIds: string[]) => {
    setLoadingUsuariosDetail(true);
    try {
      let query = supabase
        .from('usuarios')
        .select('id, nombre, email, tipo_membresia, estado, fecha_inicio, fecha_vencimiento')
        .in('id', usuarioIds);

      if (sucursalActiva?.id) {
        query = query.eq('sucursal_id', sucursalActiva.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformedUsuarios = (data || []).map((usuario: any) => {
        const initials = usuario.nombre
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);

        // Calculate progress (example: days since start / 30 days)
        const fechaInicio = usuario.fecha_inicio ? new Date(usuario.fecha_inicio) : new Date();
        const hoy = new Date();
        const diasTranscurridos = Math.floor((hoy.getTime() - fechaInicio.getTime()) / (1000 * 60 * 60 * 24));
        const progreso = Math.min(Math.floor((diasTranscurridos / 30) * 100), 100);

        return {
          id: usuario.id,
          nombre: usuario.nombre,
          email: usuario.email,
          avatar: initials,
          plan: usuario.tipo_membresia || 'Sin plan',
          estado: usuario.estado || 'Activo',
          fechaAsignacion: usuario.fecha_inicio ? new Date(usuario.fecha_inicio).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES'),
          progreso: progreso
        };
      });

      setUsuariosAsignadosDetail(transformedUsuarios);
    } catch (err: any) {
      console.error('Error fetching usuarios asignados:', err);
      setUsuariosAsignadosDetail([]);
    } finally {
      setLoadingUsuariosDetail(false);
    }
  };

  // Handle user menu actions
  const handleViewUserProfile = (userId: string) => {
    setActiveUserMenu(null);
    // Navigate to user profile
    window.REACT_APP_NAVIGATE(`/usuarios?id=${userId}`);
  };

  const handleViewUserProgress = (userId: string) => {
    setActiveUserMenu(null);
    // Navigate to progress page with user filter
    window.REACT_APP_NAVIGATE(`/progreso?usuario=${userId}`);
  };

  const handleUnassignUser = async (userId: string) => {
    if (!selectedRutinaForDetail) return;
    
    const confirmUnassign = confirm('¿Estás seguro de que deseas desasignar este usuario de la rutina?');
    if (!confirmUnassign) {
      setActiveUserMenu(null);
      return;
    }

    try {
      const newUsuariosAsignados = (selectedRutinaForDetail.usuarios_asignados || []).filter(id => id !== userId);

      const { error } = await supabase.rpc('desasignar_rutina_usuario', {
        p_rutina_id: selectedRutinaForDetail.id,
        p_usuario_id: userId,
      });

      if (error) throw error;

      // Update local state
      setUsuariosAsignadosDetail(prev => prev.filter(u => u.id !== userId));
      setSelectedRutinaForDetail(prev => prev ? {
        ...prev,
        usuariosAsignados: newUsuariosAsignados.length,
        usuarios_asignados: newUsuariosAsignados
      } : null);
      
      // Update rutinas list
      setRutinas(prev => prev.map(r => 
        r.id === selectedRutinaForDetail.id 
          ? { ...r, usuariosAsignados: newUsuariosAsignados.length, usuarios_asignados: newUsuariosAsignados }
          : r
      ));

      setActiveUserMenu(null);
    } catch (err: any) {
      console.error('Error desasignando usuario:', err);
      alert('Error al desasignar el usuario: ' + err.message);
    }
  };

  if (loading) {
    if (isEmbedView) {
      return (
        <div className="flex items-center justify-center h-64 bg-transparent">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando rutina...</p>
          </div>
        </div>
      );
    }
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando rutinas...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    if (isEmbedView) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <i className="ri-error-warning-line text-red-600 text-2xl"></i>
            <div>
              <h3 className="text-red-900 font-semibold">Error al cargar rutinas</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <i className="ri-error-warning-line text-red-600 text-2xl"></i>
            <div>
              <h3 className="text-red-900 font-semibold">Error al cargar rutinas</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const PageLayout = isEmbedView ? Fragment : AdminLayout;

  return (
    <PageLayout>
      <div className="space-y-6">
      {!isEmbedView && (
      <>
      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold">¡Rutina asignada exitosamente!</p>
              <p className="text-sm text-green-100">
                {selectedUserIds.length} usuario{selectedUserIds.length !== 1 ? 's' : ''} asignado{selectedUserIds.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}
      {exportSuccessName && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in" role="status" aria-live="polite">
          <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold">Plantilla convertida en rutina</p>
              <p className="text-sm text-green-100">
                Se creó la rutina “{exportSuccessName}”.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-1 inline-flex">
        <button
          onClick={() => setActiveTab('rutinas')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'rutinas'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Rutinas
        </button>
        <button
          onClick={() => setActiveTab('ejercicios')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'ejercicios'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Biblioteca de Ejercicios
        </button>
        <button
          onClick={() => setActiveTab('plantillas')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'plantillas'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Plantillas
        </button>
        <button
          onClick={() => setActiveTab('personalizadas')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'personalizadas'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Personalizadas
        </button>
        <button
          onClick={() => setActiveTab('familias')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'familias' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Familias
        </button>
      </div>

      {/* Search and Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder={
                activeTab === 'rutinas'
                  ? 'Buscar rutinas...'
                  : activeTab === 'plantillas'
                    ? 'Buscar plantillas...'
                    : activeTab === 'personalizadas'
                      ? 'Buscar rutinas personalizadas...'
                      : activeTab === 'familias'
                        ? 'Buscar familias...'
                    : 'Buscar ejercicios...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'rutinas' && profile?.adminId && (
              <label className="inline-flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
                <span className="text-sm font-medium text-gray-700">Mis rutinas</span>
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showOnlyMyRutinas}
                  onChange={(event) => setShowOnlyMyRutinas(event.target.checked)}
                />
                <span className="relative w-11 h-6 rounded-full bg-gray-300 transition-colors peer-checked:bg-teal-600 peer-focus-visible:ring-2 peer-focus-visible:ring-teal-500 peer-focus-visible:ring-offset-2 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5"></span>
              </label>
            )}
            <button 
              onClick={handleButtonClick}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line mr-2"></i>
              {activeTab === 'rutinas'
                ? 'Nueva Rutina'
                : activeTab === 'plantillas'
                  ? 'Nueva Plantilla'
                  : activeTab === 'personalizadas'
                    ? 'Nueva Rutina'
                    : activeTab === 'familias'
                      ? 'Nueva Familia'
                    : 'Nuevo Ejercicio'}
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'familias' ? (
        <div className="space-y-5">
          {showFamiliaForm && (
            <div className="bg-white rounded-xl border border-teal-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div><h3 className="font-bold text-gray-900">Nueva familia de rutinas</h3><p className="text-sm text-gray-500">Después podrás agregar y ordenar sus etapas.</p></div>
                <button type="button" onClick={() => setShowFamiliaForm(false)} className="p-2 rounded-lg hover:bg-gray-100"><i className="ri-close-line" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-medium text-gray-700">Nombre
                  <input value={familiaNombre} onChange={(e) => setFamiliaNombre(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Ej: Hipertrofia progresiva" />
                </label>
                <label className="text-sm font-medium text-gray-700">Plazo de aprobación del coach
                  <div className="mt-1 flex items-center gap-2"><input type="number" min={1} max={30} value={familiaDiasAprobacion} onChange={(e) => setFamiliaDiasAprobacion(Math.max(1, Math.min(30, Number(e.target.value) || 5)))} className="w-24 px-3 py-2 border border-gray-300 rounded-lg" /><span className="text-sm text-gray-500">días</span></div>
                </label>
                <label className="md:col-span-2 text-sm font-medium text-gray-700">Descripción
                  <textarea value={familiaDescripcion} onChange={(e) => setFamiliaDescripcion(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
                </label>
                <label className="md:col-span-2 flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" checked={familiaAvanceAutomatico} onChange={(e) => setFamiliaAvanceAutomatico(e.target.checked)} className="w-4 h-4" />Aplicar automáticamente si el coach no responde dentro del plazo</label>
              </div>
              <div className="mt-6 border-t border-gray-200 pt-5">
                <div className="flex items-center justify-between gap-3 mb-3"><div><h4 className="font-semibold text-gray-900">Niveles de la familia</h4><p className="text-xs text-gray-500">Selecciona la rutina que corresponde a cada nivel.</p></div><button type="button" onClick={() => setFamiliaNiveles((actuales) => [...actuales, { rutinaId: '', duracionDias: 30, sesionesMinimas: '' }])} className="px-3 py-2 rounded-lg border border-teal-200 text-teal-700 text-sm"><i className="ri-add-line mr-1" />Agregar nivel</button></div>
                <div className="space-y-3">{familiaNiveles.map((nivel, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-[6rem_minmax(0,1fr)_9rem_10rem_2.5rem] gap-3 items-end p-3 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="self-center"><span className="inline-flex px-3 py-2 rounded-lg bg-teal-100 text-teal-800 font-bold text-sm">Nivel {index + 1}</span></div>
                    <label className="text-xs font-medium text-gray-600">Rutina<select value={nivel.rutinaId} onChange={(e) => setFamiliaNiveles((actuales) => actuales.map((item, idx) => idx === index ? { ...item, rutinaId: e.target.value } : item))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"><option value="">Selecciona una rutina...</option>{rutinas.filter((r) => !r.es_copia_usuario && !r.es_plantilla && !r.creada_por_usuario).map((rutina) => <option key={rutina.id} value={String(rutina.id)}>{rutina.nombre}</option>)}</select></label>
                    <label className="text-xs font-medium text-gray-600">Duración (días)<input type="number" min={1} value={nivel.duracionDias} onChange={(e) => setFamiliaNiveles((actuales) => actuales.map((item, idx) => idx === index ? { ...item, duracionDias: Math.max(1, Number(e.target.value) || 1) } : item))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" /></label>
                    <label className="text-xs font-medium text-gray-600">Sesiones mínimas<input type="number" min={0} value={nivel.sesionesMinimas} onChange={(e) => setFamiliaNiveles((actuales) => actuales.map((item, idx) => idx === index ? { ...item, sesionesMinimas: e.target.value } : item))} placeholder="Opcional" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" /></label>
                    <button type="button" disabled={familiaNiveles.length === 1} onClick={() => setFamiliaNiveles((actuales) => actuales.filter((_, idx) => idx !== index))} className="h-10 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30" aria-label={`Eliminar nivel ${index + 1}`}><i className="ri-delete-bin-line" /></button>
                  </div>
                ))}</div>
              </div>
              <div className="flex justify-end gap-2 mt-5"><button onClick={() => setShowFamiliaForm(false)} className="px-4 py-2 border rounded-lg">Cancelar</button><button disabled={familiaSaving || !familiaNombre.trim()} onClick={crearFamilia} className="px-4 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50">{familiaSaving ? 'Guardando...' : 'Crear familia'}</button></div>
            </div>
          )}
          {familiasLoading ? <div className="py-12 text-center text-gray-500">Cargando familias...</div> : filteredFamilias.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 py-14 text-center"><i className="ri-node-tree text-5xl text-gray-300" /><p className="mt-3 text-gray-600">Aún no hay familias de rutinas.</p></div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">{filteredFamilias.map((familia) => (
              <div key={familia.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-4">
                  <div><div className="flex flex-wrap items-center gap-2"><h3 className="font-bold text-gray-900">{familia.nombre}</h3><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${familia.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{familia.activa ? 'Activa' : 'Inactiva'}</span></div><p className="text-sm text-gray-500 mt-1">{familia.descripcion || 'Sin descripción'}</p></div>
                  <div className="flex flex-wrap items-center justify-end gap-2"><button onClick={() => void handleOpenFamiliaAssignModal(familia)} disabled={!familia.activa || familia.rutina_familia_etapas.length === 0} className="px-3 py-2 border border-teal-600 text-teal-700 bg-white text-sm font-semibold rounded-lg whitespace-nowrap disabled:opacity-40"><i className="ri-user-add-line mr-1" />Asignar usuarios</button><button onClick={() => abrirEdicionFamilia(familia)} className="px-3 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-semibold rounded-lg whitespace-nowrap"><i className="ri-edit-line mr-1" />Editar familia</button><button onClick={() => { setFamiliaEditandoNivelesId(familiaEditandoNivelesId === familia.id ? null : familia.id); setNuevoNivelDraft({ rutinaId: '', duracionDias: 30, sesionesMinimas: '' }); }} className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg whitespace-nowrap"><i className="ri-add-line mr-1" />Agregar nivel</button><button onClick={() => eliminarFamilia(familia)} className="w-10 h-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" title="Eliminar familia" aria-label={`Eliminar ${familia.nombre}`}><i className="ri-delete-bin-line" /></button></div>
                </div>
                <div className="p-5">
                  {familiaEditandoId === familia.id && familiaEditDraft && <div className="mb-5 rounded-xl border border-teal-200 bg-teal-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="text-xs font-semibold text-gray-700">Nombre<input value={familiaEditDraft.nombre} onChange={(e) => setFamiliaEditDraft((actual) => actual ? { ...actual, nombre: e.target.value } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                      <label className="text-xs font-semibold text-gray-700">Plazo de aprobación (días)<input type="number" min={1} max={30} value={familiaEditDraft.diasAprobacion} onChange={(e) => setFamiliaEditDraft((actual) => actual ? { ...actual, diasAprobacion: Math.max(1, Math.min(30, Number(e.target.value) || 1)) } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                      <label className="md:col-span-2 text-xs font-semibold text-gray-700">Descripción<textarea rows={2} value={familiaEditDraft.descripcion} onChange={(e) => setFamiliaEditDraft((actual) => actual ? { ...actual, descripcion: e.target.value } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={familiaEditDraft.activa} onChange={(e) => setFamiliaEditDraft((actual) => actual ? { ...actual, activa: e.target.checked } : actual)} />Disponible para nuevas asignaciones</label>
                      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={familiaEditDraft.avanceAutomatico} onChange={(e) => setFamiliaEditDraft((actual) => actual ? { ...actual, avanceAutomatico: e.target.checked } : actual)} />Avance automático</label>
                    </div>
                    <p className="mt-3 text-xs text-teal-800">Estos cambios se aplican a futuras asignaciones. Las familias ya entregadas conservan su propia copia.</p>
                    <div className="mt-4 flex justify-end gap-2"><button onClick={() => { setFamiliaEditandoId(null); setFamiliaEditDraft(null); }} className="px-3 py-2 text-sm border border-gray-300 bg-white rounded-lg">Cancelar</button><button disabled={familiaSaving || !familiaEditDraft.nombre.trim()} onClick={() => void guardarEdicionFamilia(familia)} className="px-3 py-2 text-sm bg-teal-700 text-white rounded-lg disabled:opacity-50">{familiaSaving ? 'Guardando…' : 'Guardar cambios'}</button></div>
                  </div>}
                  <div className="flex flex-wrap gap-2 mb-4 text-xs"><span className="px-2 py-1 rounded bg-blue-50 text-blue-700">Coach: {familia.dias_aprobacion} días</span><span className={`px-2 py-1 rounded ${familia.avance_automatico ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{familia.avance_automatico ? 'Avance automático activo' : 'Requiere aprobación siempre'}</span></div>
                  {familia.rutina_familia_etapas.length === 0 ? <p className="text-sm text-gray-500 py-4 text-center">Agrega la primera rutina de la familia.</p> : <div className="space-y-2">{familia.rutina_familia_etapas.map((etapa, index) => (
                    <div key={etapa.id} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                      {familiaEditandoEtapaId === etapa.id ? <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_9rem_10rem_auto] gap-3 items-end">
                        <label className="text-xs font-medium text-gray-700">Rutina<select value={familiaEtapaDraft.rutinaId} onChange={(e) => setFamiliaEtapaDraft((actual) => ({ ...actual, rutinaId: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"><option value="">Selecciona una rutina…</option>{rutinas.filter((r) => !r.es_copia_usuario && !r.es_plantilla && !r.creada_por_usuario).map((rutina) => <option key={rutina.id} value={String(rutina.id)}>{rutina.nombre}</option>)}</select></label>
                        <label className="text-xs font-medium text-gray-700">Duración (días)<input type="number" min={1} value={familiaEtapaDraft.duracionDias} onChange={(e) => setFamiliaEtapaDraft((actual) => ({ ...actual, duracionDias: Math.max(1, Number(e.target.value) || 1) }))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                        <label className="text-xs font-medium text-gray-700">Sesiones mínimas<input type="number" min={0} value={familiaEtapaDraft.sesionesMinimas} onChange={(e) => setFamiliaEtapaDraft((actual) => ({ ...actual, sesionesMinimas: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                        <div className="flex gap-2"><button onClick={() => setFamiliaEditandoEtapaId(null)} className="px-3 py-2 text-sm border border-gray-300 bg-white rounded-lg">Cancelar</button><button disabled={familiaSaving} onClick={() => void guardarEdicionEtapaFamilia(familia, etapa.id)} className="px-3 py-2 text-sm bg-teal-700 text-white rounded-lg disabled:opacity-50">Guardar</button></div>
                      </div> : <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-teal-600 text-white grid place-items-center font-bold text-sm">{index + 1}</div><div className="flex-1 min-w-0"><p className="font-semibold text-gray-900 truncate">{etapa.rutina?.nombre || 'Rutina'}</p><p className="text-xs text-gray-500">{etapa.duracion_dias} días{etapa.sesiones_minimas != null ? ` · mínimo ${etapa.sesiones_minimas} sesiones` : ''}</p></div><button onClick={() => abrirRutinaDeFamilia(etapa.rutina_id)} className="p-2 text-slate-700 hover:bg-slate-100 rounded-lg" title="Ver y editar rutina" aria-label={`Ver rutina ${etapa.rutina?.nombre || ''}`}><i className="ri-search-eye-line" /></button><button onClick={() => abrirEdicionEtapaFamilia(etapa)} className="p-2 text-teal-700 hover:bg-teal-50 rounded-lg" title="Editar etapa"><i className="ri-edit-line" /></button><button onClick={() => eliminarEtapaFamilia(etapa.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Eliminar etapa"><i className="ri-delete-bin-line" /></button></div>}
                    </div>
                  ))}</div>}
                  {familiaEditandoNivelesId === familia.id && <div className="mt-4 p-4 rounded-xl border border-teal-200 bg-teal-50"><p className="font-semibold text-teal-950 mb-3">Nivel {familia.rutina_familia_etapas.length + 1}</p><div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_9rem_10rem] gap-3"><label className="text-xs font-medium text-gray-700">Rutina<select value={nuevoNivelDraft.rutinaId} onChange={(e) => setNuevoNivelDraft((actual) => ({ ...actual, rutinaId: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"><option value="">Selecciona una rutina...</option>{rutinas.filter((r) => !r.es_copia_usuario && !r.es_plantilla && !r.creada_por_usuario && !familia.rutina_familia_etapas.some((e) => String(e.rutina_id) === String(r.id))).map((rutina) => <option key={rutina.id} value={String(rutina.id)}>{rutina.nombre}</option>)}</select></label><label className="text-xs font-medium text-gray-700">Duración (días)<input type="number" min={1} value={nuevoNivelDraft.duracionDias} onChange={(e) => setNuevoNivelDraft((actual) => ({ ...actual, duracionDias: Math.max(1, Number(e.target.value) || 1) }))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label><label className="text-xs font-medium text-gray-700">Sesiones mínimas<input type="number" min={0} value={nuevoNivelDraft.sesionesMinimas} onChange={(e) => setNuevoNivelDraft((actual) => ({ ...actual, sesionesMinimas: e.target.value }))} placeholder="Opcional" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label></div><div className="flex justify-end gap-2 mt-3"><button onClick={() => setFamiliaEditandoNivelesId(null)} className="px-3 py-2 text-sm border border-gray-300 bg-white rounded-lg">Cancelar</button><button onClick={() => agregarEtapaFamilia(familia)} disabled={!nuevoNivelDraft.rutinaId} className="px-3 py-2 text-sm bg-teal-700 text-white rounded-lg disabled:opacity-50">Guardar nivel</button></div></div>}
                </div>
              </div>
            ))}</div>
          )}
        </div>
      ) : activeTab !== 'ejercicios' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(activeTab === 'plantillas'
            ? filteredPlantillas
            : activeTab === 'personalizadas'
              ? filteredRutinasPersonalizadas
              : filteredRutinas
          ).map((rutina) => (
            <div key={rutina.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{rutina.nombre}</h3>
                    <p className="text-sm text-gray-600">{rutina.descripcion}</p>
                    {activeTab !== 'plantillas' && rutina.creada_por_usuario && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                        <i className="ri-user-star-line"></i>
                        Personalizada por usuario
                      </span>
                    )}
                    {activeTab !== 'plantillas' && !rutina.creada_por_usuario && (
                      <span className={`inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-semibold ${
                        rutina.rutina_origen_id
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <i className={rutina.rutina_origen_id ? 'ri-file-copy-2-line' : 'ri-edit-line'}></i>
                        {rutina.rutina_origen_id ? 'Desde plantilla' : 'Creada manualmente'}
                      </span>
                    )}
                    {(rutina.sesiones || sesionesEjemplo[rutina.id]) && (
                      <div className="flex gap-1 mt-2">
                        {(rutina.sesiones || sesionesEjemplo[rutina.id])?.map(sesion => (
                          <span key={sesion.id} className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                            {sesion.id}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`w-3 h-3 rounded-full ${rutina.activa ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <i className="ri-time-line text-teal-600 text-xl mb-1"></i>
                    <p className="text-xs text-gray-600">Duración</p>
                    <p className="text-sm font-semibold text-gray-900">{rutina.duracion} min</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <i className="ri-list-check text-teal-600 text-xl mb-1"></i>
                    <p className="text-xs text-gray-600">Ejercicios</p>
                    <p className="text-sm font-semibold text-gray-900">{rutina.ejercicios}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <i className="ri-user-line text-teal-600 text-xl mb-1"></i>
                    <p className="text-xs text-gray-600">{activeTab === 'plantillas' ? 'Alcance' : 'Usuarios'}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {activeTab === 'plantillas'
                        ? (rutina.plantilla_global ? 'Global' : 'Interna')
                        : rutina.usuariosAsignados}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    rutina.nivel === 'Principiante' ? 'bg-green-100 text-green-800' :
                    rutina.nivel === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {rutina.nivel}
                  </span>
                  <div className="flex gap-2">
                    {activeTab !== 'plantillas' && (
                      <button 
                        onClick={() => handleOpenAssignModal(rutina)}
                        className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-user-add-line mr-1"></i>
                        Asignar
                      </button>
                    )}
                    {activeTab === 'plantillas' && (
                      <button
                        onClick={() => handleExportPlantillaToRutina(rutina)}
                        className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-share-forward-line mr-1"></i>
                        Exportar
                      </button>
                    )}
                    <button 
                      onClick={() => handleOpenDetailModal(rutina)}
                      className="px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Ver Detalles
                    </button>
                    <button
                      onClick={() => handleDeleteRutina(rutina)}
                      disabled={getAssignedCount(rutina) > 0 || deletingRutinaId === rutina.id}
                      title={
                        getAssignedCount(rutina) > 0
                          ? 'No se puede eliminar: tiene usuarios asignados'
                          : 'Eliminar rutina'
                      }
                      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors whitespace-nowrap ${
                        getAssignedCount(rutina) > 0 || deletingRutinaId === rutina.id
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer'
                      }`}
                      aria-label="Eliminar rutina"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ejercicio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Equipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dificultad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Músculos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recursos
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingEjercicios ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500"></span>
                        Cargando ejercicios...
                      </div>
                    </td>
                  </tr>
                ) : errorEjercicios ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-red-600">
                      {errorEjercicios}
                    </td>
                  </tr>
                ) : filteredEjercicios.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      No hay ejercicios en la biblioteca para este gimnasio.
                    </td>
                  </tr>
                ) : (
                  filteredEjercicios.map((ejercicio) => (
                    <tr key={ejercicio.id} className="hover:bg-gray-50">
                      {(() => {
                        const rutinasAsignadas = getRutinasAsignadasByExercise(ejercicio.id);
                        const estaEnUso = rutinasAsignadas.length > 0;
                        const ejemplos = rutinasAsignadas.slice(0, 2).map((r) => r.nombre).join(', ');
                        const extra = rutinasAsignadas.length > 2 ? ` y ${rutinasAsignadas.length - 2} más` : '';
                        const tooltipEliminar = estaEnUso
                          ? `No se puede eliminar: en uso en ${ejemplos}${extra}`
                          : 'Eliminar ejercicio';
                        return (
                          <>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{ejercicio.nombre}</div>
                          <div className="text-sm text-gray-500">{ejercicio.descripcion}</div>
                          <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                            ejercicio.alcance === 'privado'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {ejercicio.alcance === 'privado' ? 'Privado' : 'Catálogo general'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800">
                          {ejercicio.categoria}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ejercicio.equipo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-800' :
                          ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {ejercicio.dificultad}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {ejercicio.musculosPrincipales.slice(0, 2).join(', ')}
                          {ejercicio.musculosPrincipales.length > 2 && '...'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {ejercicio.tieneVideo && (
                            <span className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                              <i className="ri-video-line text-blue-600 text-sm"></i>
                            </span>
                          )}
                          {ejercicio.tieneAnimacion && (
                            <span className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                              <i className="ri-play-circle-line text-green-600 text-sm"></i>
                            </span>
                          )}
                          {ejercicio.tieneVideoSubido && (
                            <span className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center">
                              <i className="ri-film-line text-purple-600 text-sm"></i>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => handleOpenExerciseDetailModal(ejercicio)}
                          className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer"
                        >
                          <i className="ri-eye-line w-5 h-5 flex items-center justify-center"></i>
                        </button>
                        {canManageExercise(ejercicio) && (
                          <>
                            <button 
                              onClick={() => handleOpenEditExerciseModal(ejercicio)}
                              className="text-gray-600 hover:text-gray-900 mr-3 cursor-pointer"
                            >
                              <i className="ri-edit-line w-5 h-5 flex items-center justify-center"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteExercise(ejercicio)}
                              disabled={deletingExerciseId === ejercicio.id || estaEnUso}
                              className="text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                              title={tooltipEliminar}
                              aria-label="Eliminar ejercicio"
                            >
                              <i className="ri-delete-bin-line w-5 h-5 flex items-center justify-center"></i>
                            </button>
                          </>
                        )}
                      </td>
                          </>
                        );
                      })()}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Exercise Detail Modal */}
      {isExerciseDetailModalOpen && selectedExerciseForDetail && (
        <div className={`${isEmbedView ? 'absolute' : 'fixed'} inset-0 z-50 overflow-y-auto`}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className={`${isEmbedView ? 'absolute' : 'fixed'} inset-0 bg-black/50 transition-opacity`}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <i className="ri-boxing-line text-teal-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedExerciseForDetail.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        {selectedExerciseForDetail.categoria}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedExerciseForDetail.dificultad === 'Principiante' ? 'bg-green-100 text-green-800' :
                        selectedExerciseForDetail.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedExerciseForDetail.dificultad}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleCloseExerciseDetailModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6">
                {/* Descripción */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-file-text-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Descripción
                  </h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                    {selectedExerciseForDetail.descripcion || 'Sin descripción disponible.'}
                  </p>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <i className="ri-price-tag-3-line text-2xl text-teal-600 mb-2"></i>
                    <p className="text-xs text-gray-500 mb-1">Categoría</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExerciseForDetail.categoria}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <i className="ri-tools-line text-2xl text-teal-600 mb-2"></i>
                    <p className="text-xs text-gray-500 mb-1">Equipo</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExerciseForDetail.equipo || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <i className="ri-bar-chart-line text-2xl text-teal-600 mb-2"></i>
                    <p className="text-xs text-gray-500 mb-1">Dificultad</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExerciseForDetail.dificultad}</p>
                  </div>
                </div>

                {/* Músculos Principales */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-body-scan-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Músculos Principales
                  </h4>
                  {selectedExerciseForDetail.musculosPrincipales.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedExerciseForDetail.musculosPrincipales.map((musculo) => (
                        <span key={musculo} className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                          {musculo}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No se han especificado músculos.</p>
                  )}
                </div>

                {/* Recursos Multimedia */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-movie-2-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Recursos Multimedia
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className={`p-4 rounded-lg border ${selectedExerciseForDetail.tieneVideo ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedExerciseForDetail.tieneVideo ? 'bg-blue-200' : 'bg-gray-200'}`}>
                          <i className={`ri-video-line text-lg ${selectedExerciseForDetail.tieneVideo ? 'text-blue-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Video</p>
                          <p className={`text-xs ${selectedExerciseForDetail.tieneVideo ? 'text-blue-600' : 'text-gray-400'}`}>
                            {selectedExerciseForDetail.tieneVideo ? 'Disponible' : 'No disponible'}
                          </p>
                        </div>
                      </div>
                      {selectedExerciseForDetail.tieneVideo && selectedExerciseForDetail.urlVideo && (
                        <a 
                          href={selectedExerciseForDetail.urlVideo} 
                          target="_blank" 
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline cursor-pointer mt-1"
                        >
                          <i className="ri-external-link-line"></i>
                          Ver video
                        </a>
                      )}
                    </div>
                    <div className={`p-4 rounded-lg border ${selectedExerciseForDetail.tieneAnimacion ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedExerciseForDetail.tieneAnimacion ? 'bg-green-200' : 'bg-gray-200'}`}>
                          <i className={`ri-play-circle-line text-lg ${selectedExerciseForDetail.tieneAnimacion ? 'text-green-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Animación</p>
                          <p className={`text-xs ${selectedExerciseForDetail.tieneAnimacion ? 'text-green-600' : 'text-gray-400'}`}>
                            {selectedExerciseForDetail.tieneAnimacion ? 'Disponible' : 'No disponible'}
                          </p>
                        </div>
                      </div>
                      {selectedExerciseForDetail.tieneAnimacion && selectedExerciseForDetail.urlGif && (
                        <a 
                          href={selectedExerciseForDetail.urlGif} 
                          target="_blank" 
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline cursor-pointer mt-1"
                        >
                          <i className="ri-external-link-line"></i>
                          Ver animación
                        </a>
                      )}
                    </div>
                    <div className={`p-4 rounded-lg border ${selectedExerciseForDetail.tieneVideoSubido ? 'border-purple-200 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedExerciseForDetail.tieneVideoSubido ? 'bg-purple-200' : 'bg-gray-200'}`}>
                          <i className={`ri-film-line text-lg ${selectedExerciseForDetail.tieneVideoSubido ? 'text-purple-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Video subido</p>
                          <p className={`text-xs ${selectedExerciseForDetail.tieneVideoSubido ? 'text-purple-600' : 'text-gray-400'}`}>
                            {selectedExerciseForDetail.tieneVideoSubido ? 'Disponible' : 'No disponible'}
                          </p>
                        </div>
                      </div>
                      {selectedExerciseForDetail.tieneVideoSubido && selectedExerciseForDetail.urlVideoSubido && (
                        <a
                          href={selectedExerciseForDetail.urlVideoSubido}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline cursor-pointer mt-1"
                        >
                          <i className="ri-external-link-line"></i>
                          Ver video (loop)
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rutinas que usan este ejercicio */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-heart-pulse-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Rutinas que incluyen este ejercicio
                  </h4>
                  {(() => {
                    const rutinasConEjercicio = rutinas.filter(r => {
                      const sesionesR = r.sesiones || sesionesEjemplo[r.id] || [];
                      return sesionesR.some(s => s.ejercicios.some(e => e.ejercicioId === selectedExerciseForDetail.id));
                    });
                    return rutinasConEjercicio.length > 0 ? (
                      <div className="space-y-2">
                        {rutinasConEjercicio.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3">
                              <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-bold">
                                {r.nombre.charAt(0)}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{r.nombre}</p>
                                <p className="text-xs text-gray-500">{r.nivel} &middot; {r.duracion} min</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                handleCloseExerciseDetailModal();
                                handleOpenDetailModal(r);
                              }}
                              className="px-3 py-1.5 text-xs text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Ver rutina
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-lg">
                        Este ejercicio no est? asignado a ninguna rutina actualmente.
                      </p>
                    );
                  })()}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseExerciseDetailModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
                {canManageExercise(selectedExerciseForDetail) && (
                  <button 
                    onClick={() => {
                      handleCloseExerciseDetailModal();
                      handleOpenEditExerciseModal(selectedExerciseForDetail);
                    }}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Editar Ejercicio
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {isAssignModalOpen && (selectedRutinaForAssign || selectedFamiliaForAssign) && (
        <div className={`${isEmbedView ? 'absolute' : 'fixed'} inset-0 z-50 overflow-y-auto`}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className={`${isEmbedView ? 'absolute' : 'fixed'} inset-0 bg-black/50 transition-opacity`}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-3xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedFamiliaForAssign ? 'Asignar familia de rutinas' : 'Asignar Rutina'}</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedFamiliaForAssign?.nombre || selectedRutinaForAssign?.nombre}</p>
                </div>
                <button 
                  onClick={handleCloseAssignModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50 p-4 text-left">
                  <label htmlFor="coach-rutina" className="block text-sm font-semibold text-teal-950">
                    Coach responsable <span className="text-red-600">*</span>
                  </label>
                  <p className="mt-1 text-xs text-teal-800">
                    Quedará registrado en la rutina del socio. La plantilla seguirá mostrando a Mowe Admin como creador original.
                  </p>
                  <select
                    id="coach-rutina"
                    value={selectedCoachId}
                    onChange={(event) => setSelectedCoachId(event.target.value)}
                    disabled={loadingCoachesAsignacion}
                    className="mt-3 w-full rounded-lg border border-teal-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-60"
                    required
                  >
                    <option value="">{loadingCoachesAsignacion ? 'Cargando coaches...' : 'Selecciona un coach...'}</option>
                    {coachesAsignacion.map((coach) => (
                      <option key={coach.id} value={coach.id}>
                        {coach.nombre} · {coach.tipo_staff === 'coach_externo' ? 'Coach externo' : 'Coach interno'}
                      </option>
                    ))}
                  </select>
                  {!loadingCoachesAsignacion && coachesAsignacion.length === 0 && (
                    <p className="mt-2 text-xs font-medium text-red-700">No hay coaches activos disponibles en este gimnasio.</p>
                  )}
                </div>

                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Buscar por nombre o email..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                {/* Info Box */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-blue-600 mt-0.5"></i>
                    <div className="text-xs text-blue-700">
                      <p className="font-medium mb-1">{selectedFamiliaForAssign ? 'Sobre esta familia:' : 'Sobre esta rutina:'}</p>
                      {selectedFamiliaForAssign ? <><p className="mb-2">El usuario comenzará en el nivel 1 y avanzará según la duración y sesiones configuradas.</p><div className="flex flex-wrap gap-2">{selectedFamiliaForAssign.rutina_familia_etapas.map(etapa=><span key={etapa.id} className="px-2 py-0.5 bg-white/40 rounded">Nivel {etapa.orden}: {etapa.rutina?.nombre || 'Rutina'}</span>)}</div></> : (() => {
                        const sesionesRutina = selectedRutinaForAssign.sesiones || sesionesEjemplo[selectedRutinaForAssign.id] || [];
                        return sesionesRutina.length > 0 ? (
                          <>
                            <p className="text-xs text-blue-700 mb-2">
                              Esta rutina tiene {sesionesRutina.length} sesiones que se alternan durante la semana.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {sesionesRutina.map(sesion => (
                                <span key={sesion.id} className="px-2 py-0.5 bg-white/20 rounded text-xs font-medium">
                                  {sesion.nombre} ({sesion.ejercicios.length} ejercicios)
                                </span>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-blue-700">
                            Esta rutina contiene {selectedRutinaForAssign.ejercicios} ejercicios.
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Users List */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {selectedUserIds.length} de {activeUsersForAssign.length} usuarios activos seleccionados
                      </span>
                      <button
                        onClick={handleToggleAllUsers}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        {allFilteredUsersSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                      </button>
                    </div>
                  </div>

                  {loadingUsuarios ? (
                    <div className="p-8 text-center">
                      <i className="ri-loader-4-line text-3xl text-teal-500 animate-spin"></i>
                      <p className="text-sm text-gray-500 mt-2">Cargando usuarios...</p>
                    </div>
                  ) : filteredUsersForAssign.length === 0 ? (
                    <div className="p-8 text-center">
                      <i className="ri-user-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-500">No se encontraron usuarios</p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      {filteredUsersForAssign.map((usuario) => {
                        const evaluation = initialEvaluationsByUser[usuario.id];
                        const evaluationExpanded = expandedEvaluationUserId === usuario.id;
                        return <div key={usuario.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                          <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3">
                            <label className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(usuario.id)}
                                disabled={familiaAlreadyAssignedIds.includes(usuario.id)}
                                onChange={() => handleToggleUserSelection(usuario.id)}
                                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                              />
                              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-semibold text-teal-600">{usuario.avatar}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{usuario.nombre}</p>
                                <p className="text-xs text-gray-500 truncate">{usuario.email}</p>
                              </div>
                            </label>
                            <div className="flex flex-wrap items-center gap-2 pl-8 lg:pl-0">
                              {evaluation ? <button type="button" onClick={() => setExpandedEvaluationUserId(evaluationExpanded ? null : usuario.id)} className={`px-2.5 py-1 rounded text-xs font-semibold whitespace-nowrap ${evaluation.estado === 'requiere_revision' ? 'bg-red-50 text-red-700' : 'bg-cyan-50 text-cyan-800'}`}><i className="ri-clipboard-pulse-line mr-1" />Evaluación {new Date(`${evaluation.fecha_evaluacion}T12:00:00`).toLocaleDateString('es-CL')}<i className={`${evaluationExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} ml-1`} /></button> : <span className="px-2.5 py-1 rounded bg-gray-100 text-gray-500 text-xs font-medium whitespace-nowrap">Sin evaluación</span>}
                              {familiaAlreadyAssignedIds.includes(usuario.id) && <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium whitespace-nowrap">Ya asignado</span>}
                              <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium whitespace-nowrap">{usuario.plan}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${usuario.estado === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{usuario.estado}</span>
                            </div>
                          </div>
                          {evaluation && evaluationExpanded && <RoutineAssignmentEvaluationSummary evaluation={evaluation} />}
                        </div>;
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseAssignModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleAssignRutina}
                  disabled={!selectedCoachId || (selectedFamiliaForAssign ? newFamilyAssignmentsCount === 0 : selectedUserIds.length === 0)}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-user-add-line mr-2"></i>
                  Asignar a {selectedFamiliaForAssign ? newFamilyAssignmentsCount : selectedUserIds.length} usuario{(selectedFamiliaForAssign ? newFamilyAssignmentsCount : selectedUserIds.length) !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </>
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedRutinaForDetail && (
        <div className={`${isEmbedView ? 'absolute overflow-hidden' : 'fixed overflow-y-auto'} inset-0 z-50`}>
          <div className={`flex items-center justify-center text-center ${isEmbedView ? 'h-full p-3' : 'min-h-screen px-4 pt-4 pb-20 sm:p-0'}`}>
            <div 
              className={`${isEmbedView ? 'absolute' : 'fixed'} inset-0 bg-black/50 transition-opacity`}
            ></div>

            <div className={`relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-7xl mx-auto ${isEmbedView ? 'h-full min-h-0 flex flex-col overflow-hidden' : ''}`}>
              {/* Header */}
              <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-teal-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedRutinaForDetail.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedRutinaForDetail.nivel === 'Principiante' ? 'bg-green-100 text-green-800' :
                        selectedRutinaForDetail.nivel === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedRutinaForDetail.nivel}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedRutinaForDetail.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedRutinaForDetail.activa ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleCloseDetailModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Tabs */}
              <div className="shrink-0 px-6 pt-4 border-b border-gray-200">
                <div className="flex gap-4">
                  {[
                    { id: 'info', label: 'Información', icon: 'ri-information-line' },
                    { id: 'sesiones', label: 'Sesiones', icon: 'ri-list-check' },
                    { id: 'usuarios', label: 'Usuarios Asignados', icon: 'ri-user-line' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setDetailActiveTab(tab.id as 'info' | 'sesiones' | 'usuarios')}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                        detailActiveTab === tab.id
                          ? 'border-teal-600 text-teal-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={tab.icon}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className={`px-6 py-6 overflow-y-auto ${isEmbedView ? 'flex-1 min-h-0' : 'max-h-[60vh] min-h-[50vh]'}`}>
                {detailActiveTab === 'info' && (
                  <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-time-line text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{selectedRutinaForDetail.duracion}</p>
                        <p className="text-xs text-gray-500">Minutos por sesión</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-list-check text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{selectedRutinaForDetail.ejercicios}</p>
                        <p className="text-xs text-gray-500">Ejercicios totales</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-calendar-line text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{getRutinaSesiones(selectedRutinaForDetail.id).length}</p>
                        <p className="text-xs text-gray-500">Sesiones</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-user-line text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{selectedRutinaForDetail.usuariosAsignados}</p>
                        <p className="text-xs text-gray-500">Usuarios asignados</p>
                      </div>
                    </div>

                    {/* Configuración general */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border border-gray-200 rounded-lg bg-white">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Fecha de inicio</h4>
                        <p className="text-sm text-gray-700">
                          {selectedRutinaForDetail.fechaInicio
                            ? new Date(`${selectedRutinaForDetail.fechaInicio}T00:00:00`).toLocaleDateString('es-ES')
                            : 'No definida'}
                        </p>
                      </div>
                      <div className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                        <h4 className="text-sm font-semibold text-orange-900 mb-2">Calentamiento general</h4>
                        <div className="space-y-1 text-sm text-orange-900/90">
                          <p>
                            <span className="font-medium">Tipo:</span>{' '}
                            {selectedRutinaForDetail.calentamientoGeneral?.tipo || 'No definido'}
                          </p>
                          <p>
                            <span className="font-medium">Duración:</span>{' '}
                            {selectedRutinaForDetail.calentamientoGeneral?.duracionMin ?? 0} min
                          </p>
                          <p>
                            <span className="font-medium">Intensidad:</span>{' '}
                            {selectedRutinaForDetail.calentamientoGeneral?.intensidad || 'No definida'}
                          </p>
                          {selectedRutinaForDetail.calentamientoGeneral?.notas && (
                            <div className="pt-2">
                              <p className="text-xs font-semibold text-orange-900">Indicaciones del coach</p>
                              <p className="mt-0.5 text-xs text-orange-800/90">
                                {selectedRutinaForDetail.calentamientoGeneral.notas}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Descripción</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                        {selectedRutinaForDetail.descripcion || 'Sin descripción disponible.'}
                      </p>
                    </div>

                    {/* Resumen de sesiones */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumen de Sesiones</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {getRutinaSesiones(selectedRutinaForDetail.id).map((sesion) => (
                          <div key={sesion.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                                {sesion.id}
                              </span>
                              <span className="text-sm font-medium text-gray-900">{sesion.nombre}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span><i className="ri-list-check mr-1"></i>{sesion.ejercicios.length} ejercicios</span>
                              <span><i className="ri-time-line mr-1"></i>~{sesion.ejercicios.reduce((sum, e) => sum + (e.series * e.repeticiones * 3 + e.descanso * e.series) / 60, 0).toFixed(0)} min</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Músculos trabajados */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Músculos Trabajados</h4>
                      <div className="flex flex-wrap gap-2">
                        {Array.from(new Set(
                          getRutinaSesiones(selectedRutinaForDetail.id)
                            .flatMap(s => s.ejercicios)
                            .map(e => e.categoria)
                        )).map((categoria) => (
                          <span key={categoria} className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                            {categoria}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {detailActiveTab === 'sesiones' && (
                  <div className="space-y-4">
                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {getRutinaSesiones(selectedRutinaForDetail.id).map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setDetailActiveSesion(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            detailActiveSesion === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Session Exercises */}
                    {getRutinaSesiones(selectedRutinaForDetail.id)
                      .filter(s => s.id === detailActiveSesion)
                      .map((sesion) => (
                        <div key={sesion.id} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">{sesion.nombre}</h4>
                            <span className="text-xs text-gray-500">{sesion.ejercicios.length} ejercicios</span>
                          </div>
                          <div className="space-y-2">
                            {sesion.ejercicios.map((ej, index) => {
                              const ejercicioInfo = ejercicios.find(e => e.id === ej.ejercicioId);
                              return (
                                <div key={ej.ejercicioId} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                                        {index + 1}
                                      </span>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{ej.nombre}</p>
                                        <p className="text-xs text-gray-500">{ejercicioInfo?.descripcion}</p>
                                      </div>
                                    </div>
                                    <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                      {ej.categoria}
                                    </span>
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-4">
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.series}</p>
                                      <p className="text-xs text-gray-500">Series</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.repeticiones}</p>
                                      <p className="text-xs text-gray-500">Repeticiones</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.descanso}s</p>
                                      <p className="text-xs text-gray-500">Descanso</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.peso ?? 0}</p>
                                      <p className="text-xs text-gray-500">Peso (kg)</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.rir ?? 2}</p>
                                      <p className="text-xs text-gray-500">RIR</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">
                                        {((ej.series || 0) * (ej.repeticiones || 0) * (ej.peso || 0)).toFixed(0)} kg
                                      </p>
                                      <p className="text-xs text-gray-500">Carga estimada</p>
                                    </div>
                                  </div>
                                  <p className="mt-2 text-[11px] text-gray-500">
                                    1RM estimado: {((ej.peso || 0) * (1 + (ej.repeticiones || 0) / 30)).toFixed(1)} kg
                                  </p>
                                  {ejercicioInfo && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <p className="text-xs text-gray-500 mb-2">Músculos trabajados:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {ejercicioInfo.musculosPrincipales.map((musculo) => (
                                          <span key={musculo} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">
                                            {musculo}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {detailActiveTab === 'usuarios' && (
                  <div className="space-y-6">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 relative">
                          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                          <input
                            type="text"
                            placeholder="Buscar por nombre o email..."
                            value={usuariosAsignadosSearchTerm}
                            onChange={(e) => {
                              setUsuariosAsignadosSearchTerm(e.target.value);
                              setUsuariosAsignadosPage(1);
                            }}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                        <button 
                          onClick={() => {
                            handleCloseDetailModal();
                            handleOpenAssignModal(selectedRutinaForDetail);
                          }}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-user-add-line mr-2"></i>
                          Asignar Más
                        </button>
                      </div>
                      
                      {/* Filtros por Estado y Plan */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-medium">Filtrar por:</span>
                        </div>
                        <select
                          value={usuariosAsignadosEstadoFilter}
                          onChange={(e) => {
                            setUsuariosAsignadosEstadoFilter(e.target.value);
                            setUsuariosAsignadosPage(1);
                          }}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white"
                        >
                          <option value="">Todos los estados</option>
                          <option value="activo">Activo</option>
                          <option value="inactivo">Inactivo</option>
                        </select>
                        <select
                          value={usuariosAsignadosPlanFilter}
                          onChange={(e) => {
                            setUsuariosAsignadosPlanFilter(e.target.value);
                            setUsuariosAsignadosPage(1);
                          }}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white"
                        >
                          <option value="">Todos los planes</option>
                          {Array.from(new Set(usuariosAsignadosDetail.map(u => u.plan))).filter(Boolean).map(plan => (
                            <option key={plan} value={plan}>{plan}</option>
                          ))}
                        </select>
                        {(usuariosAsignadosEstadoFilter || usuariosAsignadosPlanFilter) && (
                          <button
                            onClick={() => {
                              setUsuariosAsignadosEstadoFilter('');
                              setUsuariosAsignadosPlanFilter('');
                              setUsuariosAsignadosPage(1);
                            }}
                            className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                          >
                            <i className="ri-close-line"></i>
                            Limpiar filtros
                          </button>
                        )}
                      </div>
                    </div>

                    {loadingUsuariosDetail ? (
                      <div className="text-center py-16">
                        <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
                        <p className="text-sm text-gray-500 mt-3">Cargando usuarios...</p>
                      </div>
                    ) : usuariosAsignadosDetail.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-user-line text-3xl text-gray-300"></i>
                        </div>
                        <p className="text-sm text-gray-500 mb-1">No hay usuarios asignados a esta rutina</p>
                        <p className="text-xs text-gray-400 mb-4">Asigna usuarios para que puedan seguir esta rutina</p>
                        <button 
                          onClick={() => {
                            handleCloseDetailModal();
                            handleOpenAssignModal(selectedRutinaForDetail);
                          }}
                          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-user-add-line mr-2"></i>
                          Asignar Usuarios
                        </button>
                      </div>
                    ) : (() => {
                      const filteredUsuarios = usuariosAsignadosDetail.filter(usuario => {
                        const matchSearch = usuario.nombre.toLowerCase().includes(usuariosAsignadosSearchTerm.toLowerCase()) ||
                          usuario.email.toLowerCase().includes(usuariosAsignadosSearchTerm.toLowerCase());
                        const matchEstado = !usuariosAsignadosEstadoFilter || usuario.estado.toLowerCase() === usuariosAsignadosEstadoFilter.toLowerCase();
                        const matchPlan = !usuariosAsignadosPlanFilter || usuario.plan === usuariosAsignadosPlanFilter;
                        return matchSearch && matchEstado && matchPlan;
                      });
                      const totalPages = Math.ceil(filteredUsuarios.length / USUARIOS_PER_PAGE);
                      const startIndex = (usuariosAsignadosPage - 1) * USUARIOS_PER_PAGE;
                      const paginatedUsuarios = filteredUsuarios.slice(startIndex, startIndex + USUARIOS_PER_PAGE);

                      return filteredUsuarios.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-filter-off-line text-3xl text-gray-300"></i>
                          </div>
                          <p className="text-sm text-gray-500 mb-1">No se encontraron usuarios</p>
                          <p className="text-xs text-gray-400">Intenta con otros filtros o términos de búsqueda</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-500">
                            Mostrando {startIndex + 1}-{Math.min(startIndex + USUARIOS_PER_PAGE, filteredUsuarios.length)} de {filteredUsuarios.length} usuarios
                            {(usuariosAsignadosEstadoFilter || usuariosAsignadosPlanFilter) && (
                              <span className="ml-2 text-teal-600">
                                (filtrado de {usuariosAsignadosDetail.length} total)
                              </span>
                            )}
                          </p>
                          {paginatedUsuarios.map((usuario) => (
                              <div key={usuario.id} className="p-5 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                                      <span className="text-sm font-semibold text-teal-600">{usuario.avatar}</span>
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">{usuario.nombre}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{usuario.email}</p>
                                      <div className="flex items-center gap-3 mt-1.5">
                                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                          {usuario.plan}
                                        </span>
                                        <span className="text-xs text-gray-400 flex items-center gap-1">
                                          <i className="ri-calendar-line text-xs"></i>
                                          Asignado: {usuario.fechaAsignacion}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6">
                                    <div className="min-w-[160px]">
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs text-gray-500">Progreso</span>
                                        <span className="text-sm font-bold text-gray-900">{usuario.progreso}%</span>
                                      </div>
                                      <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className={`h-full rounded-full transition-all ${
                                            usuario.progreso! >= 75 ? 'bg-green-500' :
                                            usuario.progreso! >= 40 ? 'bg-teal-500' :
                                            'bg-orange-400'
                                          }`}
                                          style={{ width: `${usuario.progreso}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                      usuario.estado === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {usuario.estado}
                                    </span>
                                    <div className="relative">
                                      <button 
                                        onClick={() => setActiveUserMenu(activeUserMenu === usuario.id ? null : usuario.id)}
                                        className="w-9 h-9 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                                      >
                                        <i className="ri-more-2-fill text-gray-500 text-lg"></i>
                                      </button>
                                      
                                      {activeUserMenu === usuario.id && (
                                        <>
                                          <div 
                                            className="fixed inset-0 z-10"
                                            onClick={() => setActiveUserMenu(null)}
                                          ></div>
                                          <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 z-20">
                                            <button
                                              onClick={() => handleViewUserProfile(usuario.id)}
                                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                                            >
                                              <i className="ri-user-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                                              Ver Perfil
                                            </button>
                                            <button
                                              onClick={() => handleViewUserProgress(usuario.id)}
                                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                                            >
                                              <i className="ri-line-chart-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                                              Ver Progreso
                                            </button>
                                            <div className="border-t border-gray-100 my-1"></div>
                                            <button
                                              onClick={() => handleUnassignUser(usuario.id)}
                                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                                            >
                                              <i className="ri-user-unfollow-line w-5 h-5 flex items-center justify-center"></i>
                                              Desasignar Rutina
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                          ))}

                          {/* Pagination Controls */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                              <p className="text-xs text-gray-500">
                                Página {usuariosAsignadosPage} de {totalPages}
                              </p>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setUsuariosAsignadosPage(1)}
                                  disabled={usuariosAsignadosPage === 1}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-skip-back-mini-line"></i>
                                </button>
                                <button
                                  onClick={() => setUsuariosAsignadosPage(prev => Math.max(1, prev - 1))}
                                  disabled={usuariosAsignadosPage === 1}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-arrow-left-s-line"></i>
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                  .filter(page => {
                                    if (totalPages <= 5) return true;
                                    if (page === 1 || page === totalPages) return true;
                                    return Math.abs(page - usuariosAsignadosPage) <= 1;
                                  })
                                  .map((page, idx, arr) => {
                                    const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                                    return (
                                      <span key={page} className="flex items-center">
                                        {showEllipsis && (
                                          <span className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">...</span>
                                        )}
                                        <button
                                          onClick={() => setUsuariosAsignadosPage(page)}
                                          className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                                            usuariosAsignadosPage === page
                                              ? 'bg-teal-600 text-white'
                                              : 'text-gray-600 hover:bg-gray-100'
                                          }`}
                                        >
                                          {page}
                                        </button>
                                      </span>
                                    );
                                  })}
                                <button
                                  onClick={() => setUsuariosAsignadosPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={usuariosAsignadosPage === totalPages}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-arrow-right-s-line"></i>
                                </button>
                                <button
                                  onClick={() => setUsuariosAsignadosPage(totalPages)}
                                  disabled={usuariosAsignadosPage === totalPages}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-skip-forward-mini-line"></i>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <i className="ri-calendar-line"></i>
                  <span>Creada el 15 de enero, 2024</span>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseDetailModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cerrar
                  </button>
                  <button 
                    onClick={() => handleOpenEditRutinaModal(selectedRutinaForDetail)}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Editar Rutina
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Rutina Modal */}
      {isEditRutinaModalOpen && (
        <div className={`${isEmbedView ? 'absolute overflow-hidden' : 'fixed overflow-y-auto'} inset-0 z-50`}>
          <div className={`flex items-center justify-center text-center ${isEmbedView ? 'h-full p-3' : 'min-h-screen px-4 pt-4 pb-20 sm:p-0'}`}>
            <div 
              className={`${isEmbedView ? 'absolute' : 'fixed'} inset-0 bg-black/50 transition-opacity`}
            ></div>

            <div className={`relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-7xl mx-auto ${isEmbedView ? 'h-full min-h-0 flex flex-col overflow-hidden' : ''}`}>
              {/* Header */}
              <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedRutinaForEdit?.es_plantilla ? 'Editar Plantilla' : 'Editar Rutina'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedRutinaForEdit?.es_plantilla
                      ? 'Modifica la plantilla y define si queda global para todos los usuarios.'
                      : 'Modifica la información y ejercicios de la rutina'}
                  </p>
                </div>
                <button 
                  onClick={handleCloseEditRutinaModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className={`px-6 py-4 overflow-y-auto ${isEmbedView ? 'flex-1 min-h-0' : 'max-h-[75vh]'}`}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Rutina Info */}
                  <div className="space-y-5">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                      Información de la Rutina
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Rutina <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editRutina.nombre}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: Rutina Full Body Principiante"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={editRutina.descripcion}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Describe el objetivo y características de la rutina..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel de Dificultad <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3">
                        {dificultades.map(dif => (
                          <button
                            key={dif}
                            type="button"
                            onClick={() => setEditRutina(prev => ({ ...prev, nivel: dif }))}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              editRutina.nivel === dif
                                ? dif === 'Principiante' 
                                  ? 'bg-green-600 text-white' 
                                  : dif === 'Intermedio' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración por Sesión (min)
                        </label>
                        <input
                          type="number"
                          value={editRutina.duracion}
                          onChange={(e) => setEditRutina(prev => ({ ...prev, duracion: parseInt(e.target.value) || 0 }))}
                          min={15}
                          max={180}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <label className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editRutina.activa}
                            onChange={(e) => setEditRutina(prev => ({ ...prev, activa: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Activa</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de inicio
                      </label>
                      <input
                        type="date"
                        value={editRutina.fechaInicio}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, fechaInicio: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-3">
                      <h5 className="text-sm font-semibold text-orange-900">Calentamiento general (opcional)</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-orange-900/80 mb-1">Tipo</label>
                          <select
                            value={editRutina.calentamientoGeneral.tipo}
                            onChange={(e) =>
                              setEditRutina(prev => ({
                                ...prev,
                                calentamientoGeneral: { ...prev.calentamientoGeneral, tipo: e.target.value }
                              }))
                            }
                            className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                          >
                            {tiposCalentamiento.map(tipo => (
                              <option key={tipo} value={tipo}>{tipo}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-orange-900/80 mb-1">Duración (min)</label>
                          <input
                            type="number"
                            min={0}
                            max={60}
                            value={editRutina.calentamientoGeneral.duracionMin}
                            onChange={(e) =>
                              setEditRutina(prev => ({
                                ...prev,
                                calentamientoGeneral: {
                                  ...prev.calentamientoGeneral,
                                  duracionMin: Math.max(0, parseInt(e.target.value) || 0),
                                }
                              }))
                            }
                            className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-orange-900/80 mb-1">Intensidad</label>
                        <select
                          value={editRutina.calentamientoGeneral.intensidad}
                          onChange={(e) =>
                            setEditRutina(prev => ({
                              ...prev,
                              calentamientoGeneral: { ...prev.calentamientoGeneral, intensidad: e.target.value }
                            }))
                          }
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                        >
                          {intensidadesCalentamiento.map(intensidad => (
                            <option key={intensidad} value={intensidad}>{intensidad}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-orange-900/80 mb-1">Indicaciones del coach</label>
                        <textarea
                          rows={2}
                          maxLength={220}
                          value={editRutina.calentamientoGeneral.notas}
                          onChange={(e) =>
                            setEditRutina(prev => ({
                              ...prev,
                              calentamientoGeneral: { ...prev.calentamientoGeneral, notas: e.target.value }
                            }))
                          }
                          placeholder="Ej: 5 min bici + movilidad de cadera."
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
                        />
                      </div>
                    </div>

                    {selectedRutinaForEdit?.es_plantilla && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editTemplateGlobalScope}
                            onChange={(e) => setEditTemplateGlobalScope(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Disponible para todos (global)</p>
                            <p className="text-xs text-blue-700">
                              Si está desmarcado, la plantilla queda interna para el equipo administrador.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    <VolumenMuscularResumen sesiones={editSesiones} catalogo={ejercicios} />

                    {/* Sesiones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Sesiones ({editSesiones.length})
                        </label>
                        <button
                          onClick={handleAddEditSesion}
                          disabled={editSesiones.length >= 7}
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Agregar
                        </button>
                      </div>
                      <div className="space-y-2">
                        {editSesiones.map((sesion) => (
                          <div key={sesion.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                              {sesion.id}
                            </span>
                            <input
                              type="text"
                              value={sesion.nombre}
                              onChange={(e) => handleRenameEditSesion(sesion.id, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {sesion.ejercicios.length} ej.
                            </span>
                            {editSesiones.length > 1 && (
                              <button
                                onClick={() => handleRemoveEditSesion(sesion.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                  {/* Middle Column - Session Exercises */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-list-check text-teal-600"></i>
                        Ejercicios de la Sesión
                      </h4>
                    </div>

                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {editSesiones.map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setEditSesionActiva(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            editSesionActiva === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Ejercicios de la sesión activa */}
                    {editSesionActual && (
                      <>
                        {editSesionActual.ejercicios.length === 0 ? (
                          <div className="p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                            <i className="ri-list-check text-4xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">Agrega ejercicios desde la biblioteca</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {editSesionActual.ejercicios.map((ej, index) => (
                              <div key={ej.ejercicioId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-medium">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{ej.nombre}</span>
                                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{ej.categoria}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'up')}
                                      disabled={index === 0}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-up-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'down')}
                                      disabled={index === editSesionActual.ejercicios.length - 1}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleRemoveEjercicioFromEditRutina(ej.ejercicioId)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer ml-1"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-500">Series</label>
                                    <input
                                      type="number"
                                      value={ej.series}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'series', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={10}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reps</label>
                                    <input
                                      type="number"
                                      value={ej.repeticiones}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'repeticiones', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={50}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Peso (kg)</label>
                                    <input
                                      type="number"
                                      value={ej.peso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'peso', parseFloat(e.target.value) || 0)}
                                      onBlur={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'peso', normalizarPesoKg(e.target.value))}
                                      min={0}
                                      max={500}
                                      step={PESO_STEP_KG}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Desc (s)</label>
                                    <input
                                      type="number"
                                      value={ej.descanso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'descanso', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={300}
                                      step={15}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">RIR</label>
                                    <input
                                      type="number"
                                      value={ej.rir ?? 2}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'rir', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={6}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Tempo</label>
                                    <input
                                      type="text"
                                      value={ej.tempo ?? '3-2-1'}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'tempo', e.target.value)}
                                      placeholder="3-2-1"
                                      list="tempo-options-edit"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Formato: bajada-pausa-subida</p>
                                    <datalist id="tempo-options-edit">
                                      {tempoOptions.map((opt) => (
                                        <option key={opt} value={opt} />
                                      ))}
                                    </datalist>
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Carga estimada</label>
                                    <div className="px-2 py-1 border border-gray-200 bg-gray-100 rounded text-xs text-gray-700 h-[30px] flex items-center">
                                      {`${((ej.series || 0) * (ej.repeticiones || 0) * (ej.peso || 0)).toFixed(0)} kg`}
                                    </div>
                                  </div>
                                </div>
                                <p className="mt-2 text-[11px] text-gray-500">
                                  1RM estimado: {((ej.peso || 0) * (1 + (ej.repeticiones || 0) / 30)).toFixed(1)} kg
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Column - Exercise Library */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-book-2-line text-teal-600"></i>
                      Biblioteca de Ejercicios
                    </h4>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          placeholder="Buscar ejercicio..."
                          value={editEjercicioSearchTerm}
                          onChange={(e) => setEditEjercicioSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <select
                        value={editCategoriaFiltro}
                        onChange={(e) => setEditCategoriaFiltro(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        {ejerciciosDisponiblesEdit.length === 0 ? (
                          <div className="p-6 text-center">
                            <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">No hay ejercicios disponibles</p>
                          </div>
                        ) : (
                          ejerciciosDisponiblesEdit.map((ejercicio) => (
                            <div 
                              key={ejercicio.id} 
                              className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{ejercicio.nombre}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-700' :
                                      ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {ejercicio.dificultad}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded">{ejercicio.categoria}</span>
                                    <span>{ejercicio.equipo}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddEjercicioToEditRutina(ejercicio)}
                                  className="ml-3 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                    </div>

                  <div>
                    <ProgresionSemanalEditor
                      baseSesiones={editSesiones}
                      value={editProgresionSemanal}
                      onChange={setEditProgresionSemanal}
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="text-sm text-gray-500">
                  {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i>
                      {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0)} ejercicios en {editSesiones.length} sesión{editSesiones.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseEditRutinaModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveEditRutina}
                    disabled={!editRutina.nombre || !editRutina.nivel || editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) === 0}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Exercise Modal */}
      {isEditExerciseModalOpen && selectedExerciseForEdit && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Editar Ejercicio</h3>
                  <p className="text-sm text-gray-500 mt-1">Modifica la información del ejercicio</p>
                </div>
                <button 
                  onClick={handleCloseEditExerciseModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Ejercicio <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={selectedExerciseForEdit.nombre}
                      onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, nombre: e.target.value } : null)}
                      placeholder="Ej: Press de Banca"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={selectedExerciseForEdit.descripcion}
                      onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, descripcion: e.target.value } : null)}
                      placeholder="Describe brevemente el ejercicio..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoría <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedExerciseForEdit.categoria}
                        onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, categoria: e.target.value } : null)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Equipo Necesario
                      </label>
                      <select
                        value={selectedExerciseForEdit.equipo}
                        onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, equipo: e.target.value } : null)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {equipos.map(eq => (
                          <option key={eq} value={eq}>{eq}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Nivel de Dificultad <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      {dificultades.map(dif => (
                        <button
                          key={dif}
                          type="button"
                          onClick={() => setSelectedExerciseForEdit(prev => prev ? { ...prev, dificultad: dif } : null)}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            selectedExerciseForEdit.dificultad === dif
                               ? dif === 'Principiante' 
                                 ? 'bg-green-600 text-white' 
                                 : dif === 'Intermedio' 
                                   ? 'bg-orange-500 text-white' 
                                   : 'bg-red-600 text-white'
                               : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Músculos Principales
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowGestionMusculos((prev) => !prev)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                          title="Administrar grupos musculares"
                        >
                          <i className="ri-settings-3-line"></i>
                          Administrar
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowNuevoMusculoInput((prev) => !prev)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100 cursor-pointer"
                          title="Agregar grupo muscular"
                        >
                          <i className="ri-add-line"></i>
                          Agregar
                        </button>
                      </div>
                    </div>
                    {showGestionMusculos && (
                      <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2 max-h-48 overflow-auto">
                        {gruposMusculares.length === 0 ? (
                          <p className="text-xs text-gray-500">No hay grupos en catálogo para este gimnasio.</p>
                        ) : (
                          gruposMusculares.map((grupo) => (
                            <div key={grupo.id} className="flex items-center gap-2">
                              {editingMusculoId === grupo.id ? (
                                <input
                                  value={editingMusculoNombre}
                                  onChange={(e) => setEditingMusculoNombre(e.target.value)}
                                  className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                              ) : (
                                <div className="flex-1 text-xs text-gray-700">{grupo.nombre}</div>
                              )}
                              {editingMusculoId === grupo.id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveEditMusculo(grupo)}
                                    disabled={procesandoMusculoId === grupo.id}
                                    className="px-2 py-1 text-xs rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 cursor-pointer"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditMusculo}
                                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleStartEditMusculo(grupo)}
                                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeactivateMusculo(grupo)}
                                    disabled={procesandoMusculoId === grupo.id}
                                    className="px-2 py-1 text-xs rounded border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60 cursor-pointer"
                                  >
                                    Desactivar
                                  </button>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    {showNuevoMusculoInput && (
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          value={nuevoMusculoNombre}
                          onChange={(e) => setNuevoMusculoNombre(e.target.value)}
                          placeholder="Ej: Serrato Anterior"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddMusculo('edit')}
                          disabled={guardandoNuevoMusculo}
                          className="px-3 py-2 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 cursor-pointer"
                        >
                          {guardandoNuevoMusculo ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {musculosDisponibles.map(musculo => (
                        <button
                          key={musculo}
                          type="button"
                          onClick={() => handleEditExerciseMusculoToggle(musculo)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            selectedExerciseForEdit.musculosPrincipales.includes(musculo)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {musculo}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instrucciones (una por línea)
                      </label>
                      <textarea
                        value={(selectedExerciseForEdit.instrucciones || []).join('\n')}
                        onChange={(e) =>
                          setSelectedExerciseForEdit((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  instrucciones: e.target.value
                                    .split('\n')
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                }
                              : null
                          )
                        }
                        placeholder={"Ej:\nColoca la espalda recta\nTira la polea hacia el pecho\nControla el regreso"}
                        rows={5}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Consejos (opcional, uno por línea)
                      </label>
                      <textarea
                        value={(selectedExerciseForEdit.consejos || []).join('\n')}
                        onChange={(e) =>
                          setSelectedExerciseForEdit((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  consejos: e.target.value
                                    .split('\n')
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                }
                              : null
                          )
                        }
                        placeholder={"Ej:\nNo encorves la espalda\nNo uses impulso\nAjusta el peso para controlar"}
                        rows={5}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Si lo dejas vacío, en mobile se mostrarán consejos genéricos.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Recursos Multimedia
                    </label>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedExerciseForEdit.tieneVideo}
                            disabled={
                              !selectedExerciseForEdit.tieneVideo &&
                              (selectedExerciseForEdit.tieneAnimacion || selectedExerciseForEdit.tieneVideoSubido)
                            }
                            onChange={(e) => handleToggleEditExerciseMedia('video', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <i className="ri-video-line text-blue-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Explicativo</span>
                          </div>
                        </label>
                        {selectedExerciseForEdit.tieneVideo && (
                          <div>
                            <input
                              type="url"
                              value={selectedExerciseForEdit.urlVideo || ''}
                              onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, urlVideo: e.target.value } : null)}
                              placeholder="https://youtube.com/watch?v=..."
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {selectedExerciseForEdit.urlVideo && (
                              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs text-blue-700 mb-1">Vista previa:</p>
                                <a 
                                  href={selectedExerciseForEdit.urlVideo} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline break-all"
                                >
                                  {selectedExerciseForEdit.urlVideo}
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedExerciseForEdit.tieneAnimacion}
                            disabled={
                              !selectedExerciseForEdit.tieneAnimacion &&
                              (selectedExerciseForEdit.tieneVideo || selectedExerciseForEdit.tieneVideoSubido)
                            }
                            onChange={(e) => handleToggleEditExerciseMedia('gif', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <i className="ri-play-circle-line text-green-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Animación GIF</span>
                          </div>
                        </label>
                        {selectedExerciseForEdit.tieneAnimacion && (
                          <div>
                            <input
                              type="url"
                              value={selectedExerciseForEdit.urlGif || ''}
                              onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, urlGif: e.target.value } : null)}
                              placeholder="https://ejemplo.com/animacion.gif"
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {selectedExerciseForEdit.urlGif && (
                              <div className="mt-2 p-3 bg-green-50 rounded-lg">
                                <p className="text-xs text-green-700 mb-2">Vista previa:</p>
                                <img 
                                  src={selectedExerciseForEdit.urlGif} 
                                  alt="Animación del ejercicio"
                                  className="w-full max-w-xs rounded-lg border border-green-200"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedExerciseForEdit.tieneVideoSubido}
                            disabled={
                              !selectedExerciseForEdit.tieneVideoSubido &&
                              (selectedExerciseForEdit.tieneVideo || selectedExerciseForEdit.tieneAnimacion)
                            }
                            onChange={(e) => handleToggleEditExerciseMedia('upload', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                              <i className="ri-film-line text-purple-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Subido (máx. 10 MB)</span>
                          </div>
                        </label>
                        {selectedExerciseForEdit.tieneVideoSubido && (
                          <div className="space-y-2">
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/ogg,video/quicktime,.mov"
                              onChange={async (e) => {
                                const file = e.currentTarget.files?.[0] || null;
                                try {
                                  await handleEditExerciseVideoFileChange(file);
                                } catch (err: any) {
                                  alert(err?.message || 'No se pudo procesar el video.');
                                  e.currentTarget.value = '';
                                }
                              }}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-purple-100 file:text-purple-700"
                            />
                            {(selectedExerciseForEdit.urlVideoSubido || editExerciseVideoFile) && (
                              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                {editExerciseVideoFile ? (
                                  <p className="text-xs text-purple-700">
                                    Nuevo archivo: <span className="font-medium">{editExerciseVideoFile.name}</span>
                                  </p>
                                ) : (
                                  <a
                                    href={selectedExerciseForEdit.urlVideoSubido}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-purple-700 hover:underline break-all"
                                  >
                                    Video actual: {selectedExerciseForEdit.urlVideoSubido}
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseEditExerciseModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveEditExercise}
                  disabled={!selectedExerciseForEdit.nombre || !selectedExerciseForEdit.categoria || !selectedExerciseForEdit.dificultad}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-2"></i>
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Exercise Modal - Update to include new fields */}
      {isNewExerciseModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Nuevo Ejercicio</h3>
                  <p className="text-sm text-gray-500 mt-1">Agrega un nuevo ejercicio a la biblioteca</p>
                </div>
                <button 
                  onClick={handleCloseNewExerciseModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
                <div className="space-y-5">
                  {/* Nombre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Ejercicio <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newExercise.nombre}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej: Press de Banca"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={newExercise.descripcion}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, descripcion: e.target.value }))}
                      placeholder="Describe brevemente el ejercicio..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoría <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newExercise.categoria}
                        onChange={(e) => setNewExercise(prev => ({ ...prev, categoria: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Equipo Necesario
                      </label>
                      <select
                        value={newExercise.equipo}
                        onChange={(e) => setNewExercise(prev => ({ ...prev, equipo: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {equipos.map(eq => (
                          <option key={eq} value={eq}>{eq}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nivel de Dificultad <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      {dificultades.map(dif => (
                        <button
                          key={dif}
                          type="button"
                          onClick={() => setNewExercise(prev => ({ ...prev, dificultad: dif }))}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            newExercise.dificultad === dif
                              ? dif === 'Principiante' 
                                ? 'bg-green-600 text-white' 
                                : dif === 'Intermedio' 
                                  ? 'bg-orange-500 text-white' 
                                  : 'bg-red-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Músculos Principales
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowGestionMusculos((prev) => !prev)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                          title="Administrar grupos musculares"
                        >
                          <i className="ri-settings-3-line"></i>
                          Administrar
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowNuevoMusculoInput((prev) => !prev)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100 cursor-pointer"
                          title="Agregar grupo muscular"
                        >
                          <i className="ri-add-line"></i>
                          Agregar
                        </button>
                      </div>
                    </div>
                    {showGestionMusculos && (
                      <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2 max-h-48 overflow-auto">
                        {gruposMusculares.length === 0 ? (
                          <p className="text-xs text-gray-500">No hay grupos en catálogo para este gimnasio.</p>
                        ) : (
                          gruposMusculares.map((grupo) => (
                            <div key={grupo.id} className="flex items-center gap-2">
                              {editingMusculoId === grupo.id ? (
                                <input
                                  value={editingMusculoNombre}
                                  onChange={(e) => setEditingMusculoNombre(e.target.value)}
                                  className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                              ) : (
                                <div className="flex-1 text-xs text-gray-700">{grupo.nombre}</div>
                              )}
                              {editingMusculoId === grupo.id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveEditMusculo(grupo)}
                                    disabled={procesandoMusculoId === grupo.id}
                                    className="px-2 py-1 text-xs rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 cursor-pointer"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditMusculo}
                                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleStartEditMusculo(grupo)}
                                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeactivateMusculo(grupo)}
                                    disabled={procesandoMusculoId === grupo.id}
                                    className="px-2 py-1 text-xs rounded border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60 cursor-pointer"
                                  >
                                    Desactivar
                                  </button>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    {showNuevoMusculoInput && (
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          value={nuevoMusculoNombre}
                          onChange={(e) => setNuevoMusculoNombre(e.target.value)}
                          placeholder="Ej: Serrato Anterior"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddMusculo('new')}
                          disabled={guardandoNuevoMusculo}
                          className="px-3 py-2 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 cursor-pointer"
                        >
                          {guardandoNuevoMusculo ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {musculosDisponibles.map(musculo => (
                        <button
                          key={musculo}
                          type="button"
                          onClick={() => handleNewExerciseMusculoToggle(musculo)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            newExercise.musculosPrincipales.includes(musculo)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {musculo}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Recursos Multimedia
                    </label>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={newExercise.tieneVideo}
                            disabled={!newExercise.tieneVideo && (newExercise.tieneAnimacion || newExercise.tieneVideoSubido)}
                            onChange={(e) => handleToggleNewExerciseMedia('video', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <i className="ri-video-line text-blue-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Explicativo</span>
                          </div>
                        </label>
                        {newExercise.tieneVideo && (
                          <input
                            type="url"
                            value={newExercise.urlVideo || ''}
                            onChange={(e) => setNewExercise(prev => ({ ...prev, urlVideo: e.target.value }))}
                            placeholder="https://youtube.com/watch?v=..."
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={newExercise.tieneAnimacion}
                            disabled={!newExercise.tieneAnimacion && (newExercise.tieneVideo || newExercise.tieneVideoSubido)}
                            onChange={(e) => handleToggleNewExerciseMedia('gif', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <i className="ri-play-circle-line text-green-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Animación GIF</span>
                          </div>
                        </label>
                        {newExercise.tieneAnimacion && (
                          <input
                            type="url"
                            value={newExercise.urlGif || ''}
                            onChange={(e) => setNewExercise(prev => ({ ...prev, urlGif: e.target.value }))}
                            placeholder="https://ejemplo.com/animacion.gif"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={newExercise.tieneVideoSubido}
                            disabled={!newExercise.tieneVideoSubido && (newExercise.tieneVideo || newExercise.tieneAnimacion)}
                            onChange={(e) => handleToggleNewExerciseMedia('upload', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                              <i className="ri-film-line text-purple-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Subido (máx. 10 MB)</span>
                          </div>
                        </label>
                        {newExercise.tieneVideoSubido && (
                          <div className="space-y-2">
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/ogg,video/quicktime,.mov"
                              onChange={async (e) => {
                                const file = e.currentTarget.files?.[0] || null;
                                try {
                                  await handleNewExerciseVideoFileChange(file);
                                } catch (err: any) {
                                  alert(err?.message || 'No se pudo procesar el video.');
                                  e.currentTarget.value = '';
                                }
                              }}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-purple-100 file:text-purple-700"
                            />
                            {(newExerciseVideoFile || newExercise.urlVideoSubido) && (
                              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                {newExerciseVideoFile ? (
                                  <p className="text-xs text-purple-700">
                                    Archivo: <span className="font-medium">{newExerciseVideoFile.name}</span>
                                  </p>
                                ) : (
                                  <p className="text-xs text-purple-700 break-all">Video: {newExercise.urlVideoSubido}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseNewExerciseModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveExercise}
                  disabled={!newExercise.nombre || !newExercise.categoria || !newExercise.dificultad}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-2"></i>
                  Guardar Ejercicio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update New Rutina Modal - Add peso field */}
      {isNewRutinaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{isTemplateMode ? 'Nueva Plantilla' : 'Nueva Rutina'}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {isTemplateMode
                      ? 'Crea una plantilla reutilizable. Puedes marcarla como global para todos los usuarios.'
                      : 'Crea una rutina con múltiples sesiones de entrenamiento'}
                  </p>
                </div>
                <button 
                  onClick={handleCloseNewRutinaModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="px-6 py-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Rutina Info */}
                  <div className="space-y-5">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                      Información de la Rutina
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Rutina <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newRutina.nombre}
                        onChange={(e) => setNewRutina(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: Rutina Full Body Principiante"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={newRutina.descripcion}
                        onChange={(e) => setNewRutina(prev => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Describe el objetivo y características de la rutina..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel de Dificultad <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3">
                        {dificultades.map(dif => (
                          <button
                            key={dif}
                            type="button"
                            onClick={() => setNewRutina(prev => ({ ...prev, nivel: dif }))}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              newRutina.nivel === dif
                                ? dif === 'Principiante' 
                                  ? 'bg-green-600 text-white' 
                                  : dif === 'Intermedio' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración por Sesión (min)
                        </label>
                        <input
                          type="number"
                          value={newRutina.duracion}
                          onChange={(e) => setNewRutina(prev => ({ ...prev, duracion: parseInt(e.target.value) || 0 }))}
                          min={15}
                          max={180}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <label className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={newRutina.activa}
                            onChange={(e) => setNewRutina(prev => ({ ...prev, activa: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Activa</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de inicio
                      </label>
                      <input
                        type="date"
                        value={newRutina.fechaInicio}
                        onChange={(e) => setNewRutina(prev => ({ ...prev, fechaInicio: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-3">
                      <h5 className="text-sm font-semibold text-orange-900">Calentamiento general (opcional)</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-orange-900/80 mb-1">Tipo</label>
                          <select
                            value={newRutina.calentamientoGeneral.tipo}
                            onChange={(e) =>
                              setNewRutina(prev => ({
                                ...prev,
                                calentamientoGeneral: { ...prev.calentamientoGeneral, tipo: e.target.value }
                              }))
                            }
                            className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                          >
                            {tiposCalentamiento.map(tipo => (
                              <option key={tipo} value={tipo}>{tipo}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-orange-900/80 mb-1">Duración (min)</label>
                          <input
                            type="number"
                            min={0}
                            max={60}
                            value={newRutina.calentamientoGeneral.duracionMin}
                            onChange={(e) =>
                              setNewRutina(prev => ({
                                ...prev,
                                calentamientoGeneral: {
                                  ...prev.calentamientoGeneral,
                                  duracionMin: Math.max(0, parseInt(e.target.value) || 0),
                                }
                              }))
                            }
                            className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-orange-900/80 mb-1">Intensidad</label>
                        <select
                          value={newRutina.calentamientoGeneral.intensidad}
                          onChange={(e) =>
                            setNewRutina(prev => ({
                              ...prev,
                              calentamientoGeneral: { ...prev.calentamientoGeneral, intensidad: e.target.value }
                            }))
                          }
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                        >
                          {intensidadesCalentamiento.map(intensidad => (
                            <option key={intensidad} value={intensidad}>{intensidad}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-orange-900/80 mb-1">Indicaciones del coach</label>
                        <textarea
                          rows={2}
                          maxLength={220}
                          value={newRutina.calentamientoGeneral.notas}
                          onChange={(e) =>
                            setNewRutina(prev => ({
                              ...prev,
                              calentamientoGeneral: { ...prev.calentamientoGeneral, notas: e.target.value }
                            }))
                          }
                          placeholder="Ej: 5 min trotadora + movilidad articular."
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
                        />
                      </div>
                    </div>

                    {isTemplateMode && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={templateGlobalScope}
                            onChange={(e) => setTemplateGlobalScope(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Disponible para todos (global)</p>
                            <p className="text-xs text-blue-700">
                              Si está desmarcado, la plantilla queda interna para el equipo administrador.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    <VolumenMuscularResumen sesiones={sesiones} catalogo={ejercicios} />

                    {/* Sesiones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Sesiones ({sesiones.length})
                        </label>
                        <button
                          onClick={handleAddSesion}
                          disabled={sesiones.length >= 7}
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Agregar
                        </button>
                      </div>
                      <div className="space-y-2">
                        {sesiones.map((sesion) => (
                          <div key={sesion.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                              {sesion.id}
                            </span>
                            <input
                              type="text"
                              value={sesion.nombre}
                              onChange={(e) => handleRenameSesion(sesion.id, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {sesion.ejercicios.length} ej.
                            </span>
                            {sesiones.length > 1 && (
                              <button
                                onClick={() => handleRemoveSesion(sesion.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-blue-600 mt-0.5"></i>
                        <div className="text-xs text-blue-700">
                          <p className="font-medium mb-1">Sesiones de entrenamiento:</p>
                          <p className="text-xs text-blue-700 mb-2">
                            Esta rutina tendrá {sesiones.length} sesión{sesiones.length !== 1 ? 'es' : ''} que se alternarán durante la semana.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {sesiones.map(sesion => (
                              <span key={sesion.id} className="px-2 py-0.5 bg-white/20 rounded text-xs font-medium">
                                {sesion.nombre} ({sesion.ejercicios.length} ejercicio{sesion.ejercicios.length !== 1 ? 's' : ''})
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Session Exercises with peso field */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-list-check text-teal-600"></i>
                        Ejercicios de la Sesión
                      </h4>
                    </div>

                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {sesiones.map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setSesionActiva(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            sesionActiva === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {sesionActual && (
                      <>
                        {sesionActual.ejercicios.length === 0 ? (
                          <div className="p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                            <i className="ri-list-check text-4xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">Agrega ejercicios desde la biblioteca</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {sesionActual.ejercicios.map((ej, index) => (
                              <div key={ej.ejercicioId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-medium">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{ej.nombre}</span>
                                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{ej.categoria}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleMoveEjercicio(index, 'up')}
                                      disabled={index === 0}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-up-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleMoveEjercicio(index, 'down')}
                                      disabled={index === sesionActual.ejercicios.length - 1}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleRemoveEjercicioFromRutina(ej.ejercicioId)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer ml-1"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-500">Series</label>
                                    <input
                                      type="number"
                                      value={ej.series}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'series', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={10}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reps</label>
                                    <input
                                      type="number"
                                      value={ej.repeticiones}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'repeticiones', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={50}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Peso (kg)</label>
                                    <input
                                      type="number"
                                      value={ej.peso}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'peso', parseFloat(e.target.value) || 0)}
                                      onBlur={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'peso', normalizarPesoKg(e.target.value))}
                                      min={0}
                                      max={500}
                                      step={PESO_STEP_KG}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Desc (s)</label>
                                    <input
                                      type="number"
                                      value={ej.descanso}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'descanso', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={300}
                                      step={15}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">RIR</label>
                                    <input
                                      type="number"
                                      value={ej.rir ?? 2}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'rir', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={6}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Tempo</label>
                                    <input
                                      type="text"
                                      value={ej.tempo ?? '3-2-1'}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'tempo', e.target.value)}
                                      placeholder="3-2-1"
                                      list="tempo-options-create"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Formato: bajada-pausa-subida</p>
                                    <datalist id="tempo-options-create">
                                      {tempoOptions.map((opt) => (
                                        <option key={opt} value={opt} />
                                      ))}
                                    </datalist>
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Carga estimada</label>
                                    <div className="px-2 py-1 border border-gray-200 bg-gray-100 rounded text-xs text-gray-700 h-[30px] flex items-center">
                                      {`${((ej.series || 0) * (ej.repeticiones || 0) * (ej.peso || 0)).toFixed(0)} kg`}
                                    </div>
                                  </div>
                                </div>
                                <p className="mt-2 text-[11px] text-gray-500">
                                  1RM estimado: {((ej.peso || 0) * (1 + (ej.repeticiones || 0) / 30)).toFixed(1)} kg
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Column - Exercise Library */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-book-2-line text-teal-600"></i>
                      Biblioteca de Ejercicios
                    </h4>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          placeholder="Buscar ejercicio..."
                          value={ejercicioSearchTerm}
                          onChange={(e) => setEjercicioSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <select
                        value={categoriaFiltro}
                        onChange={(e) => setCategoriaFiltro(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        {ejerciciosDisponibles.length === 0 ? (
                          <div className="p-6 text-center">
                            <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">No hay ejercicios disponibles</p>
                          </div>
                        ) : (
                          ejerciciosDisponibles.map((ejercicio) => (
                            <div 
                              key={ejercicio.id} 
                              className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{ejercicio.nombre}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-700' :
                                      ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {ejercicio.dificultad}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded">{ejercicio.categoria}</span>
                                    <span>{ejercicio.equipo}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddEjercicioToRutina(ejercicio)}
                                  className="ml-3 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="p-3 bg-teal-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-teal-600 mt-0.5"></i>
                        <div className="text-xs text-teal-700">
                          <p className="font-medium mb-1">Consejos:</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Incluye calentamiento al inicio</li>
                            <li>Alterna grupos musculares</li>
                            <li>Ajusta según el nivel</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <ProgresionSemanalEditor baseSesiones={sesiones} value={progresionSemanal} onChange={setProgresionSemanal}/>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="text-sm text-gray-500">
                  {sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i>
                      {sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0)} ejercicios en {sesiones.length} sesión{sesiones.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseNewRutinaModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveRutina}
                    disabled={!newRutina.nombre || !newRutina.nivel || sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) === 0}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-save-line mr-2"></i>
                    {isTemplateMode ? 'Guardar Plantilla' : 'Guardar Rutina'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legacy duplicated modal disabled: keep single source of truth above */}
      {legacyEditModalEnabled && isEditRutinaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Editar Rutina</h3>
                  <p className="text-sm text-gray-500 mt-1">Modifica la información y ejercicios de la rutina</p>
                </div>
                <button 
                  onClick={handleCloseEditRutinaModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Rutina Info */}
                  <div className="space-y-5">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                      Información de la Rutina
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Rutina <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editRutina.nombre}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: Rutina Full Body Principiante"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={editRutina.descripcion}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Describe el objetivo y características de la rutina..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel de Dificultad <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3">
                        {dificultades.map(dif => (
                          <button
                            key={dif}
                            type="button"
                            onClick={() => setEditRutina(prev => ({ ...prev, nivel: dif }))}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              editRutina.nivel === dif
                                ? dif === 'Principiante' 
                                  ? 'bg-green-600 text-white' 
                                  : dif === 'Intermedio' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración por Sesión (min)
                        </label>
                        <input
                          type="number"
                          value={editRutina.duracion}
                          onChange={(e) => setEditRutina(prev => ({ ...prev, duracion: parseInt(e.target.value) || 0 }))}
                          min={15}
                          max={180}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <label className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editRutina.activa}
                            onChange={(e) => setEditRutina(prev => ({ ...prev, activa: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Activa</span>
                        </label>
                      </div>
                    </div>

                    <VolumenMuscularResumen sesiones={editSesiones} catalogo={ejercicios} />

                    {/* Sesiones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Sesiones ({editSesiones.length})
                        </label>
                        <button
                          onClick={handleAddEditSesion}
                          disabled={editSesiones.length >= 7}
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Agregar
                        </button>
                      </div>
                      <div className="space-y-2">
                        {editSesiones.map((sesion) => (
                          <div key={sesion.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                              {sesion.id}
                            </span>
                            <input
                              type="text"
                              value={sesion.nombre}
                              onChange={(e) => handleRenameEditSesion(sesion.id, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {sesion.ejercicios.length} ej.
                            </span>
                            {editSesiones.length > 1 && (
                              <button
                                onClick={() => handleRemoveEditSesion(sesion.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Session Exercises */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-list-check text-teal-600"></i>
                        Ejercicios de la Sesión
                      </h4>
                    </div>

                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {editSesiones.map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setEditSesionActiva(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            editSesionActiva === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Ejercicios de la sesión activa */}
                    {editSesionActual && (
                      <>
                        {editSesionActual.ejercicios.length === 0 ? (
                          <div className="p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                            <i className="ri-list-check text-4xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">Agrega ejercicios desde la biblioteca</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {editSesionActual.ejercicios.map((ej, index) => (
                              <div key={ej.ejercicioId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-medium">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{ej.nombre}</span>
                                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{ej.categoria}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'up')}
                                      disabled={index === 0}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-up-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'down')}
                                      disabled={index === editSesionActual.ejercicios.length - 1}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleRemoveEjercicioFromEditRutina(ej.ejercicioId)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer ml-1"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-6 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-500">Series</label>
                                    <input
                                      type="number"
                                      value={ej.series}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'series', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={10}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reps</label>
                                    <input
                                      type="number"
                                      value={ej.repeticiones}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'repeticiones', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={50}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Peso (kg)</label>
                                    <input
                                      type="number"
                                      value={ej.peso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'peso', parseFloat(e.target.value) || 0)}
                                      onBlur={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'peso', normalizarPesoKg(e.target.value))}
                                      min={0}
                                      max={500}
                                      step={PESO_STEP_KG}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Desc (s)</label>
                                    <input
                                      type="number"
                                      value={ej.descanso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'descanso', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={300}
                                      step={15}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">RIR</label>
                                    <input
                                      type="number"
                                      value={ej.rir ?? 2}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'rir', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={6}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Tempo</label>
                                    <input
                                      type="text"
                                      value={ej.tempo ?? '3-2-1'}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'tempo', e.target.value)}
                                      placeholder="3-2-1"
                                      list="tempo-options-edit-embedded"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Formato: bajada-pausa-subida</p>
                                    <datalist id="tempo-options-edit-embedded">
                                      {tempoOptions.map((opt) => (
                                        <option key={opt} value={opt} />
                                      ))}
                                    </datalist>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Column - Exercise Library */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-book-2-line text-teal-600"></i>
                      Biblioteca de Ejercicios
                    </h4>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          placeholder="Buscar ejercicio..."
                          value={editEjercicioSearchTerm}
                          onChange={(e) => setEditEjercicioSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <select
                        value={editCategoriaFiltro}
                        onChange={(e) => setEditCategoriaFiltro(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        {ejerciciosDisponiblesEdit.length === 0 ? (
                          <div className="p-6 text-center">
                            <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">No hay ejercicios disponibles</p>
                          </div>
                        ) : (
                          ejerciciosDisponiblesEdit.map((ejercicio) => (
                            <div 
                              key={ejercicio.id} 
                              className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{ejercicio.nombre}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-700' :
                                      ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {ejercicio.dificultad}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded">{ejercicio.categoria}</span>
                                    <span>{ejercicio.equipo}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddEjercicioToEditRutina(ejercicio)}
                                  className="ml-3 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="text-sm text-gray-500">
                  {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i>
                      {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0)} ejercicios en {editSesiones.length} sesión{editSesiones.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseEditRutinaModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveEditRutina}
                    disabled={!editRutina.nombre || !editRutina.nivel || editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) === 0}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </PageLayout>
  );
}

function RoutineAssignmentEvaluationSummary({ evaluation }: { evaluation: EvaluacionInicialResumen }) {
  const experienceLabels: Record<string,string> = { nunca:'Nunca', menos_3_meses:'Menos de 3 meses', '3_6_meses':'3–6 meses', '6_12_meses':'6–12 meses', '1_2_anos':'1–2 años', mas_2_anos:'Más de 2 años' };
  const frequencyLabels: Record<string,string> = { '1':'1 día/semana', '2':'2 días/semana', '3':'3 días/semana', '4':'4 días/semana', '5_mas':'5 o más días/semana', no_corresponde:'No corresponde' };
  const ankle = (value:number|null, nt:boolean) => nt ? 'NT' : value === null ? '—' : `${Number(value).toFixed(1)} cm`;
  const hasPain = evaluation.dolor_sintoma || evaluation.bisagra_dolor || evaluation.hombro_dolor_derecho || evaluation.hombro_dolor_izquierdo
    || [evaluation.sentadilla_puntaje, evaluation.bisagra_puntaje, evaluation.hombro_derecho_puntaje, evaluation.hombro_izquierdo_puntaje].includes('0');
  return <div className="mx-4 mb-3 rounded-xl border border-cyan-200 bg-cyan-50/70 p-4">
    <div className="flex flex-wrap items-center justify-between gap-2"><div><p className="text-xs font-bold uppercase tracking-wide text-cyan-800">Última evaluación funcional</p><p className="mt-1 text-sm font-semibold text-cyan-950">Nivel: {evaluation.nivel_entrenamiento ? evaluation.nivel_entrenamiento.charAt(0).toUpperCase() + evaluation.nivel_entrenamiento.slice(1) : 'No informado'}</p><p className="mt-1 text-sm text-gray-700">{experienceLabels[evaluation.experiencia_gimnasio || ''] || 'Experiencia no informada'} · {frequencyLabels[evaluation.frecuencia_semanal || ''] || 'Frecuencia no informada'}</p></div><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${evaluation.estado === 'requiere_revision' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{evaluation.estado === 'requiere_revision' ? 'Requiere revisión' : 'Completa'}</span></div>
    {hasPain && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-800"><i className="ri-alarm-warning-line mr-1" />Existe dolor o síntoma registrado. Revisar antes de asignar.</div>}
    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
      <div className="rounded-lg bg-white p-2"><span className="block text-gray-500">Sentadilla</span><strong className="text-gray-900">{evaluation.sentadilla_puntaje || '—'}</strong></div>
      <div className="rounded-lg bg-white p-2"><span className="block text-gray-500">Bisagra</span><strong className="text-gray-900">{evaluation.bisagra_puntaje || '—'}</strong></div>
      <div className="rounded-lg bg-white p-2"><span className="block text-gray-500">Tobillos D / I</span><strong className="text-gray-900">{ankle(evaluation.tobillo_derecho_cm,evaluation.tobillo_derecho_nt)} / {ankle(evaluation.tobillo_izquierdo_cm,evaluation.tobillo_izquierdo_nt)}</strong></div>
      <div className="rounded-lg bg-white p-2"><span className="block text-gray-500">Hombros D / I</span><strong className="text-gray-900">{evaluation.hombro_derecho_puntaje || '—'} / {evaluation.hombro_izquierdo_puntaje || '—'}</strong></div>
    </div>
    {(evaluation.dolor_detalle || evaluation.observaciones_generales) && <p className="mt-3 text-xs text-gray-700"><strong>Observaciones:</strong> {[evaluation.dolor_detalle,evaluation.observaciones_generales].filter(Boolean).join(' · ')}</p>}
    <p className="mt-2 text-[11px] text-gray-500">Información de apoyo para revisión profesional; no constituye diagnóstico ni selección automática de rutina.</p>
  </div>;
}
